import {
    ECONode,
    ECOTree,
    OrgPart,
    OrgSecurityGroup,
    Organization,
    deepCopy,
    flatten,
    unFlatten,
} from '@newgenus/common';
import { TreeBuilderService } from '../../../services/tree-builder.service';
import { inject, Injectable } from '@angular/core';
import { applyLinkedPosts } from '../linked-posts';
import { boardRootPartKey, mayJoinDetachedGroup } from '../detached-group-rules';

/**
 * OrgBoardBuilderService - Builds and manages the organization board tree structure
 *
 * ## Positions Toggle (`isPositionsOn`)
 *
 * The `isPositionsOn` flag controls whether "positions" (sections and units) are displayed
 * in the organization board tree. When OFF, only executives, divisions, and departments are shown.
 *
 * ### IMPORTANT: State Management
 *
 * This service is a **singleton** (providedIn: 'root'), so `isPositionsOn` persists across
 * the entire application. This can cause issues if multiple components modify it.
 *
 * **Problem Scenario (now fixed):**
 * 1. User has Positions toggle OFF in org-board
 * 2. User opens hierarchy dialog → dialog sets `isPositionsOn = true` to show all positions
 * 3. User triggers a reorder → Firestore updates → org-board rebuilds tree
 * 4. Tree rebuilds using `isPositionsOn = true` from step 2 (dialog still open)
 * 5. Result: Tree shows positions even though toggle is OFF
 *
 * **Solution:**
 * - The `organization-board.component` EXPLICITLY passes `{ isPositionsOn: this.isPositionsSwitchOn }`
 *   to `insertPositionsToEcoTree()` to ensure its local toggle state is respected.
 * - The `org-hierarchy-dialog` saves and restores the previous `isPositionsOn` state on close.
 *
 * ### Usage Guidelines:
 *
 * 1. **Organization Board Component**: Always pass `{ isPositionsOn: this.isPositionsSwitchOn }`
 *    when calling `insertPositionsToEcoTree()` to maintain control of its own toggle state.
 *
 * 2. **Dialogs (e.g., OrgHierarchyDialog)**: If you need to temporarily change `isPositionsOn`:
 *    - Save the previous state: `this.previousState = this.orgBuilderService.isPositionsOn`
 *    - Set your desired state: `this.orgBuilderService.isPositionsOn = true`
 *    - Restore in ngOnDestroy: `this.orgBuilderService.isPositionsOn = this.previousState`
 *
 * 3. **New Components**: Prefer passing explicit config to `insertPositionsToEcoTree()` rather
 *    than relying on the service's `isPositionsOn` state.
 */
@Injectable({
    providedIn: 'root',
})
export class OrgBoardBuilderService {
    public treeBuilderService = inject(TreeBuilderService);

    public heightsDivision: any[] = [];
    public heightsExecutive: any[] = [];
    public heightsDepartment: any[] = [];

    /**
     * Controls whether sections and units are displayed in the tree.
     *
     * ⚠️ WARNING: This is shared singleton state. See class documentation for proper usage.
     * Prefer passing explicit `{ isPositionsOn }` config to `insertPositionsToEcoTree()`.
     */
    isPositionsOn = false;

    public buildOrg(org: Organization, treeId: string) {
        return OrgTree.withOrg(org, '', treeId, org.orgUsers, true);
    }

    public insertPositionsToEcoTree(
        orgTree: OrgTree,
        ecoTree: ECOTree,
        config: Partial<{
            width: number;
            height: number;
            color: string;
            background: string;
            linkColor: string;
            isPositionsOn: boolean;
        }> = {}
    ) {
        // Reset height arrays to avoid accumulating values from previous builds
        this.heightsDivision = [];
        this.heightsExecutive = [];
        this.heightsDepartment = [];

        // Apply defaults for any missing config values
        const fullConfig = {
            width: config.width ?? 300,
            height: config.height ?? 150,
            color: config.color ?? fromStringToHex('black'),
            background: config.background ?? fromStringToHex('white'),
            linkColor: config.linkColor ?? fromStringToHex('red'),
            isPositionsOn: config.isPositionsOn,
        };

        let positions = orgTree.positions;

        // Use explicit isPositionsOn from config if provided, otherwise fall back to service state
        // This allows the org-board component to explicitly control the state
        if (fullConfig.isPositionsOn !== undefined) {
            this.isPositionsOn = fullConfig.isPositionsOn;
        }
        // Note: If isPositionsOn is not in config, we preserve the existing this.isPositionsOn value

        // const positions = orgTree.positions;
        // // remove all positions that are sections or units from positions -
        // // so it does not render as a node
        // // (these will be inside departments as sectionsInDepartment array and unitsInSection array inside sectionsInDepartment).

        // console.log("🚀 ~ this.isPositionsOn:", this.isPositionsOn)
        if (!this.isPositionsOn) {
            // console.log("Boooom!");
            positions = positions.filter(
                (position) => (position as any).partType !== 'section' && (position as any).partType !== 'unit'
            );
        }

        // Always filter posts whose parent is a division, executive, or department — they render inside the parent card
        const inlineParentKeys = new Set(
            positions
                .filter(
                    (p) =>
                        (p as any).partType === 'division' ||
                        (p as any).partType === 'executive' ||
                        (p as any).partType === 'department'
                )
                .map((p) => p.partKey)
        );
        positions = positions.filter((position) => {
            if ((position as any).partType === 'post' && inlineParentKeys.has(position.parentPartKey)) {
                return false; // rendered inside parent card via postsInParent
            }
            return true;
        });

        // Map child positions to parent positions using the partKey and parentPartKey.
        const parentChildMap = new Map<string, string[]>();
        positions
            // Sort by depth to ensure that parents are processed before children.
            .sort((a, b) => a.depth - b.depth)
            .forEach((position) => {
                if (position.parentPartKey) {
                    const children = parentChildMap.get(position.parentPartKey) || [];
                    children.push(position.partKey);
                    parentChildMap.set(position.parentPartKey, children);
                }
            });

        // Create position array with parents and children.
        const relationalPositions: Position[] = positions
            .sort((a, b) => a.depth - b.depth)
            .map((position) => {
                const children = parentChildMap.get(position.partKey) || [];
                const childrenData = positions.filter((p) => children.includes(p.partKey));
                const parents = positions.filter((p) => p.partKey === position.parentPartKey);
                const parentsData = parents.length > 0 ? parents : undefined;

                // console.log('position: ', position);
                return {
                    ...position,
                    children: childrenData,
                    parents: parentsData,
                };
            });

        // Shift all the children into the parents. If there are no parents, then the position is a root position.
        relationalPositions.forEach((position) => {
            if (position.parents) {
                position.parents[0].children = position.parents[0].children || [];
                position.parents[0].children.push(position);
            }
        });

        const rootPosition = relationalPositions.find((position) => !position.parents);

        // Create the tree structure.
        this.recursivelyGetTheHighestHeight(ecoTree, rootPosition);
        // console.log('heightsDivision: ', this.heightsDivision);
        this.heightsDivision.sort((a, b) => b - a);
        // console.log('heightsDivision 2: ', this.heightsDivision);
        this.heightsExecutive.sort((a, b) => b - a);
        this.heightsDepartment.sort((a, b) => b - a);
        // console.log('heightsExecutive: ', this.heightsExecutive);
        // console.log('heightsDepartment: ', this.heightsDepartment);

        const parent = {
            id: -1,
            width: 300,
            height: 150,
            color: fromStringToHex('black'),
            background: fromStringToHex('white'),
            linkColor: fromStringToHex('red'),
            divisionHighestHeight: this.heightsDivision[0],
            executiveHighestHeight: this.heightsExecutive[0],
            departmentHighestHeight: this.heightsDepartment[0],
        };

        this.recursivelyAddNodes(ecoTree, rootPosition, parent);
        // order from highest to lowest
        // console.log('#*#*#*#*#*#*#*#**** ecoTree: ', ecoTree);
        ecoTree.UpdateTree();
        // console.log('updated ecoTree: ', ecoTree);
    }

    // NOTE: This method computes initial text-based heights but does NOT account for
    // sections/units/posts content. The final department height is overridden by
    // updateNodeDimensions() in organization-board.component.ts which traverses
    // sectionsInDepartment data built by appendSectionsAndUnitsDetailsToParts().
    // See: docs/ORG-BOARD-HEIGHT-CALCULATION-SPEC.md
    private recursivelyGetTheHighestHeight(tree: ECOTree, node: any, parent: any = null) {
        // console.log("recursivelyGetTheHighestHeight - node: ", JSON.stringify(deepCopy(node)));
        const test = {
            id: node.id,
            name: node.name,
            partType: node.partType,
            parentPartKey: node.parentPartKey,
            data: node.data ? node.data : 'undefined', // Captures the current state of node.data
            linkColor: node.linkColor,
            background: node.background,
            color: node.color,
            width: node.width,
            height: node.height,
            childrenIds: node.children ? node.children.map((child: any) => child.id) : undefined, // Snapshot of child IDs
            childrenCount: node.children ? node.children.length : 0, // Retain useful count
            selected: node.selected,
            userDetailsArray: node.userDetailsArray ? 'present' : 'not present', // Indicate if userDetailsArray exists
            subTitle: node.subTitle ? node.subTitle : 'not present', // Capture subTitle if it exists
        };
        // console.log("recursivelyGetTheHighestHeight - node snapshot: ",  JSON.stringify(deepCopy(test)));

        parent = parent || {
            id: -1,
            width: 300,
            height: 150,
            color: fromStringToHex('black'),
            background: fromStringToHex('white'),
            linkColor: fromStringToHex('red'),
        };
        node.data = { ...node, children: null, parents: null };
        node.data.userDetailsArray = node.data.userDetailsArray || [];
        node.width = node.width || parent.width;
        node.height = node.height || parent.height;
        node.id = tree.nDatabaseNodes.length;

        // // if(node.isExecutive || node.partType === "division") {
        // if(node.partType === "division") {
        //     // apply this only for divisions
        //     console.log('node.isExecutive: ', node.isExecutive);
        //     console.log('node: ', node);
        //     node.width = node.width * node.children.length;

        //     // console.log("****************** BOOOM 1 ****************** ", node.partType, " - ", node.height);
        // } else {
        //     node.width = 300;
        // }

        node.color = fromStringToHex(node?.colour?.color || parent.color);
        node.background = fromStringToHex(node?.colour?.background || parent.background);
        node.linkColor = fromStringToHex(node?.colour?.linkColor || parent.background);
        if (node.data?.colour) {
            node.data.colour.color = fromStringToHex(node.data.colour.color || node.color);
            node.data.colour.background = fromStringToHex(node.data.colour.background || node.background);
            node.data.colour.linkColor = fromStringToHex(node.data.colour.linkColor || node.linkColor);
        }

        const firstLine = (node?.id || '') + (node.userDetailsArray ? node.userDetailsArray[0].name : '');
        const secondLine = node.userDetailsArray
            ? node.userDetailsArray[0].primaryPositionSecurityGroupsForUser[0]?.name || ''
            : '';
        const thirdLine = ''; // node.data?.user?.securityGroup?.name || '';
        let fourthLine = '';
        const fifthLine = '';
        if (node.userDetailsArray && node.userDetailsArray.length > 0) {
            node.userDetailsArray.forEach((user: UserDetails) => {
                fourthLine += user?.name ? user.name + ' ' : '';
            });
        }
        // if (node.data?.heldFromAboveUid) {
        //     fifthLine = node.data.heldFromAboveUserName;
        // }

        // Calculate height based on the amount of text in the node.data.
        // The container should minimum be 80px high.
        // The height should be 20px per line of text.
        // Width is set to 300px with a padding of 10px.
        // Characters per line should be 25, except for firstLine where the font size is larger, there we will consider 20 characters per line.
        const characterLength =
            firstLine.length + secondLine.length + thirdLine.length + fourthLine.length + fifthLine.length;
        let calculatedHeight = 170 + 45 * Math.ceil(characterLength / 25);
        calculatedHeight = calculatedHeight < 136 ? 136 : calculatedHeight; // minimum height of 136px

        if (node.partType === 'division') {
            this.heightsDivision.push(calculatedHeight);
            // console.log("****************** BOOOM 1 ****************** ", node.partType, " - ", calculatedHeight);
        }
        if (node.partType === 'executive') {
            this.heightsExecutive.push(calculatedHeight);
            // console.log("****************** BOOOM 2 ****************** ", node.partType, " - ", calculatedHeight);
        }
        if (node.partType === 'department') {
            this.heightsDepartment.push(calculatedHeight);
            // console.log("****************** BOOOM 3 ****************** ", node.partType, " - ", calculatedHeight);
        }

        // tree.add(
        //     node.id,
        //     parent.id,
        //     node.title,
        //     node.width,
        //     calculatedHeight,
        //     // node.height,
        //     node.color,
        //     node.background,
        //     node.linkColor,
        //     node.data,
        //     node.selected,
        //     node.isChecked,
        // );

        if (node.children) {
            node.children.forEach((x: any) => {
                this.recursivelyGetTheHighestHeight(tree, x, node);
            });
        }
    }

    // NOTE: Department heights set here (departmentHighestHeight + 200) are initial rough
    // estimates. They are overridden by updateNodeDimensions() in the component which
    // does a proper traversal of sections/units/posts.
    // See: docs/ORG-BOARD-HEIGHT-CALCULATION-SPEC.md
    private recursivelyAddNodes(tree: ECOTree, node: any, parent: any) {
        // console.log("recursivelyAddNodes - node: ", node);
        if (parent.divisionHighestHeight) {
            node.divisionHighestHeight = parent.divisionHighestHeight;
        }
        if (parent.executiveHighestHeight) {
            node.executiveHighestHeight = parent.executiveHighestHeight;
        }
        if (parent.departmentHighestHeight) {
            node.departmentHighestHeight = parent.departmentHighestHeight;
        }
        parent = parent || {
            id: -1,
            width: 300,
            height: 150,
            color: fromStringToHex('black'),
            background: fromStringToHex('white'),
            linkColor: fromStringToHex('red'),
        };
        node.data = { ...node, children: null, parents: null };
        node.data.userDetailsArray = node.data.userDetailsArray || [];
        node.width = node.width || parent.width;
        node.height = node.height || parent.height;
        node.id = tree.nDatabaseNodes.length;

        // if(node.isExecutive || node.partType === "division") {
        if (node.partType === 'division') {
            // Width scales with the division's CONTAINER children (departments).
            // Inline posts are filtered out before the relational maps are
            // built, so a division holding ONLY posts has no children array —
            // it must still render as one card (factor 1), not crash the board.
            node.width = node.width * Math.max(1, node.children?.length ?? 0);
        } else {
            node.width = 300;
        }

        if (node.partType === 'division') {
            // Division headers (above-card2) are taller: ~100px vs 40px for departments
            // Also need to account for the mainTitle and name in the header
            const mainTitleLength = (node.mainTitle || '').length;
            const divisionNameLength = (node.name || '').length;
            // mainTitle uses font-size 1.8rem (~29px) - fits ~12 chars per line in 300px width
            // name uses font-size 1.4rem (~22px) - fits ~15 chars per line
            const mainTitleLines = Math.ceil(mainTitleLength / 12) || 1;
            const nameLines = Math.ceil(divisionNameLength / 15) || 1;
            const headerExtraHeight = (mainTitleLines - 1) * 30 + (nameLines - 1) * 24;

            node.height = node.divisionHighestHeight + 250 + headerExtraHeight;
            // console.log("DIVISION HEIGHT DEBUG: ", node.data?.name,
            //     " - mainTitle:", node.mainTitle, "(", mainTitleLength, "chars,", mainTitleLines, "lines)",
            //     " - name:", node.name, "(", divisionNameLength, "chars,", nameLines, "lines)",
            //     " - headerExtraHeight:", headerExtraHeight,
            //     " - divisionHighestHeight:", node.divisionHighestHeight,
            //     " - final height:", node.height);
        }
        if (node.partType === 'executive') {
            node.height = node.executiveHighestHeight + 200;
        }
        if (node.partType === 'department') {
            node.height = node.departmentHighestHeight + 200;
        }
        if (node.partType === 'unit') {
            node.height = 200;
        }

        node.color = fromStringToHex(node?.colour?.color || parent.color);
        node.background = fromStringToHex(node?.colour?.background || parent.background);
        node.linkColor = fromStringToHex(node?.colour?.linkColor || parent.background);
        if (node.data?.colour) {
            node.data.colour.color = fromStringToHex(node.data.colour.color || node.color);
            node.data.colour.background = fromStringToHex(node.data.colour.background || node.background);
            node.data.colour.linkColor = fromStringToHex(node.data.colour.linkColor || node.linkColor);
        }

        // const firstLine = (node?.id || '') + (node.userDetailsArray ? node.userDetailsArray[0].name : '');
        // const secondLine = node.userDetailsArray ? node.userDetailsArray[0].primaryPositionSecurityGroupsForUser[0]?.name || '' : '';
        // const thirdLine = ''; // node.data?.user?.securityGroup?.name || '';
        // let fourthLine = '', fifthLine = '';
        // if (node.userDetailsArray && node.userDetailsArray.length > 0) {
        //     node.userDetailsArray.forEach((user: UserDetails) => {
        //         fourthLine += user?.name ? user.name + ' ' : '';
        //     });
        // }
        // if (node.data?.heldFromAboveUid) {
        //     fifthLine = node.data.heldFromAboveUserName;
        // }

        // Calculate height based on the amount of text in the node.data.
        // The container should minimum be 80px high.
        // The height should be 20px per line of text.
        // Width is set to 300px with a padding of 10px.
        // Characters per line should be 25, except for firstLine where the font size is larger, there we will consider 20 characters per line.
        // const characterLength = firstLine.length + secondLine.length + thirdLine.length + fourthLine.length + fifthLine.length;
        // let calculatedHeight = 170 + (45 * Math.ceil(characterLength / 25));
        // calculatedHeight = calculatedHeight < 136 ? 136 : calculatedHeight; // minimum height of 136px

        // if(node.partType === "division") {
        //     this.heightsDivision.push(calculatedHeight);
        // }
        // if(node.isExecutive) {
        //     this.heightsExecutive.push(calculatedHeight);
        // }
        // if(node.partType === "department") {
        //     this.heightsDepartment.push(calculatedHeight);
        // }

        // console.log('node.height: ', node.height , " - ", node.width, " - ", node.data.name);
        tree.add(
            node.id,
            parent.id,
            node.title,
            node.width,
            // calculatedHeight,
            node.height,
            node.color,
            node.background,
            node.linkColor,
            node.data,
            node.selected,
            node.isChecked
        );

        if (node.children) {
            node.children.forEach((x: any) => {
                this.recursivelyAddNodes(tree, x, node);
            });
        }
    }

    /**
     * Builds a tree object based on the hierarchy of positions.
     * Optional to pass in a part key to start from.
     *
     * @param {string} [partKey] - The part key to start from.
     */
    public buildPositionsTreeFromPart(org: Organization, partKey?: string): any {
        // console.log('🔍 DEBUG: buildPositionsTreeFromPart called with:', { partKey, orgPartsCount: org.orgParts?.length });

        const part = deepCopy(
            partKey ? org.orgParts?.find((pt) => pt.partKey === partKey) : org.orgParts?.find((pt) => pt.id === 1)
        );

        if (!part) {
            // console.log('🔍 DEBUG: No starting part found, returning empty object');
            return {};
        }

        // console.log('🔍 DEBUG: Starting part found:', {
        //     name: part.name,
        //     partKey: part.partKey,
        //     depth: part.depth,
        //     id: part.id
        // });

        // Debug: Track Department 19 specifically
        const isDept19 = part.name === 'Department 19';
        // if (isDept19) {
        //     console.log('🔍 DEPT 19 - Starting buildPositionsTreeFromPart for:', part.name, 'partKey:', part.partKey);
        // }

        // find the orgs with user on them
        const orgsWithSecurityGroups = [org];
        let unFlattened: any = {};

        for (const org of orgsWithSecurityGroups) {
            this.treeBuilderService.securityGroupObject = null; // reset the securityGroupObject on orgService

            const orgParts = org.orgParts;

            // console.log('🔍 DEBUG: About to call appendAllChildPositionsToOrgParts with:', {
            //     orgPartsCount: orgParts?.length,
            //     startingPart: part.name
            // });

            // if (isDept19) {
            //     console.log('🔍 DEPT 19 - orgParts count:', orgParts?.length);
            //     const dept19Children = orgParts?.filter(p => p.parentPartKey === part.partKey);
            //     console.log('🔍 DEPT 19 - Direct children from orgParts:', dept19Children?.map(c => ({ name: c.name, partKey: c.partKey, partType: (c as any).partType })));
            // }

            const positionsWillInheritFromSecurityGroupAbove2 =
                this.treeBuilderService.appendAllChildPositionsToOrgParts(orgParts, { orgPosition: part });

            // console.log('🔍 DEBUG: appendAllChildPositionsToOrgParts returned:', {
            //     count: positionsWillInheritFromSecurityGroupAbove2?.length,
            //     items: positionsWillInheritFromSecurityGroupAbove2?.map((p: OrgPart) => ({
            //         name: p.name,
            //         partKey: p.partKey,
            //         depth: p.depth,
            //         parentPartKey: p.parentPartKey
            //     }))
            // });

            // if (isDept19) {
            //     console.log('🔍 DEPT 19 - positionsWillInheritFromSecurityGroupAbove2:', positionsWillInheritFromSecurityGroupAbove2?.map((p: OrgPart) => ({ name: p.name, partKey: p.partKey, partType: p.partType, depth: p.depth })));
            // }

            // console.log("🚀 ~ buildPositionsTreeFromPart ~ positionsWillInheritFromSecurityGroupAbove2:", positionsWillInheritFromSecurityGroupAbove2);

            // find the first section of the path in partToStartFrom and identify the substring section to remove
            const partToStartFromPath = deepCopy(part.path);
            if (!partToStartFromPath) {
                console.warn('buildPositionsTreeFromPart: part.path is undefined, skipping', {
                    partKey: part.partKey,
                    name: part.name,
                });
                continue;
            }
            const partToStartFromPathSplit = partToStartFromPath.split('.');

            let partToStartFromPathSplitString = '';
            for (let i = 0; i < partToStartFromPathSplit.length - 1; i++) {
                partToStartFromPathSplitString += partToStartFromPathSplit[i] + '.';
            }

            // process the parts/positions securityGroup will inherit from the securityGroup above
            const positionsCopy = deepCopy(positionsWillInheritFromSecurityGroupAbove2);
            const positions = this.treeBuilderService.buildTreeObjectForAllSecurityGroupsForPositions(
                positionsCopy,
                partToStartFromPathSplitString,
                org,
                org.key,
                part,
                'tree5'
            );

            // if (isDept19) {
            //     console.log('🔍 DEPT 19 - After buildTreeObjectForAllSecurityGroupsForPositions:', positions?.map((p: OrgPart) => ({ name: p.name, partKey: p.partKey, partType: p.partType })));
            // }

            // console.log("🚀 ~ buildPositionsTreeFromPart ~ positions 1:", positions);

            this.treeBuilderService.extractSecurityGroupsForInheritedPositions(positions);
            // console.log("🚀 ~ buildPositionsTreeFromPart ~ positions 2:", positions);

            this.treeBuilderService.buildSecurityGroupsRelationshipsObject(
                positions,
                partToStartFromPathSplitString,
                org,
                part,
                'tree5'
            );
            // console.log("🚀 ~ buildPositionsTreeFromPart ~ positions 3:", positions);

            const newFlatObject: any = createNewOrgTreeRelationshipsFlatObject(positions);

            // if (isDept19) {
            //     console.log('🔍 DEPT 19 - newFlatObject keys:', Object.keys(newFlatObject));
            //     console.log('🔍 DEPT 19 - About to call unFlatten...');
            // }

            // console.log("🚀 ~ buildPositionsTreeFromPart ~ newFlatObject:", newFlatObject)
            unFlattened = unFlatten(newFlatObject);

            // if (isDept19) {
            //     console.log('🔍 DEPT 19 - Final unFlattened structure after unFlatten:');
            //     console.log('🔍 DEPT 19 - unFlattened keys:', Object.keys(unFlattened));
            //
            //     // Log the structure recursively to see nesting
            //     function logStructure(obj: any, prefix = '', depth = 0) {
            //         if (depth > 5) return; // Prevent infinite recursion
            //
            //         Object.keys(obj).forEach(key => {
            //             if (key === 'children' && Array.isArray(obj[key])) {
            //                 console.log(`${prefix}${key}: [${obj[key].length} children]`);
            //                 obj[key].forEach((child: any, index: number) => {
            //                     console.log(`${prefix}  [${index}] ${child.data?.name || child.name || 'unnamed'}`);
            //                     if (child.children) {
            //                         logStructure(child, prefix + '    ', depth + 1);
            //                     }
            //                 });
            //             } else if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
            //                 console.log(`${prefix}${key}: {object}`);
            //                 logStructure(obj[key], prefix + '  ', depth + 1);
            //             } else {
            //                 console.log(`${prefix}${key}: ${obj[key]}`);
            //             }
            //         });
            //     }
            //
            //     logStructure(unFlattened, '🔍 DEPT 19 - ');
            // }

            // console.log("🚀 ~ buildPositionsTreeFromPart ~ unFlattened:", unFlattened);

            // removeChildrenSecurityTrees(unFlattened);
        }

        /**
         * Creates a new flat object representing the organizational tree relationships
         * by processing the given positions array. This function builds a flat structure
         * of paths and their corresponding values based on the hierarchical relationships
         * and security groups of the positions.
         *
         * @param positionsCopy - An array of position objects representing the organizational structure.
         * Each position object contains details such as `partKey`, `parentPartKey`, `path`,
         * `securityGroupsFlat`, `inheritedSecurityGroupsFlat`, and `positionUsersFlat`.
         *
         * @returns A flat object where each key represents a path in the organizational tree
         * and each value corresponds to the associated data (e.g., security groups, inherited security groups,
         * or users with security groups).
         *
         * @remarks
         * - The function processes each position to determine its children and builds paths
         *   for security groups, inherited security groups, and users with security groups.
         * - Paths are manipulated to reflect the hierarchical structure, and new paths are
         *   generated for children and other related data.
         * - The function also handles cases where positions have no children but still contain
         *   security groups or other related data.
         * - Debugging logs are included throughout the function to trace the processing steps.
         */
        function createNewOrgTreeRelationshipsFlatObject(positionsCopy: any) {
            // console.log("🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ positionsCopy:", positionsCopy);

            // Debug: Check if we have Department 19 data
            // const dept19 = positionsCopy.find((p: any) => p.name === 'Department 19');
            // const section1 = positionsCopy.find((p: any) => p.name === 'Section 1');
            // const unit1 = positionsCopy.find((p: any) => p.name === 'Unit 1');

            // if (dept19) {
            //     console.log('🔍 TREE BUILD - Department 19 in positionsCopy:', {
            //         name: dept19.name,
            //         partKey: dept19.partKey,
            //         depth: dept19.depth,
            //         path: dept19.path
            //     });
            // }
            // if (section1) {
            //     console.log('🔍 TREE BUILD - Section 1 in positionsCopy:', {
            //         name: section1.name,
            //         partKey: section1.partKey,
            //         parentPartKey: section1.parentPartKey,
            //         depth: section1.depth,
            //         path: section1.path
            //     });
            // }
            // if (unit1) {
            //     console.log('🔍 TREE BUILD - Unit 1 in positionsCopy:', {
            //         name: unit1.name,
            //         partKey: unit1.partKey,
            //         parentPartKey: unit1.parentPartKey,
            //         depth: unit1.depth,
            //         path: unit1.path
            //     });
            // }

            const newFlat: any = {};
            for (const part of positionsCopy) {
                // console.log("2222222222 🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ part:", part);
                let pathResult = '';

                // get all the parts where the parenPartKey is equals to the partKey of the current part
                const children = positionsCopy.filter((o: any) => o.parentPartKey === part.partKey);

                // Debug parent-child relationships for our target parts
                // if (part.name === 'Department 19' || part.name === 'Section 1' || part.name === 'Unit 1') {
                //     console.log(`🔍 TREE BUILD - Children of "${part.name}":`, children.map((c: any) => ({
                //         name: c.name,
                //         partKey: c.partKey,
                //         parentPartKey: c.parentPartKey,
                //         depth: c.depth
                //     })));
                // }

                // console.log("2222222222 🚀 ~ ", part.name, " createNewOrgTreeRelationshipsFlatObject ~ children:", children);

                // let index = 0;
                // let childPath = "";

                // if (children.length > 0 || part.securityGroupsArray.length > 0) {

                //     console.log("2222222222 🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ children.length > 0 || part.securityGroupsArray.length > 0:", children.length, part.securityGroupsArray.length);
                //     // build the org positions tree flat object by adding the securityGroups to the flat object using the paths to manipulate the paths
                //     if (children.length > 0) {
                //         index = children.length - 1;
                //         childPath = children[0].path;

                //         // new path for the current parts new children to add
                //         // remove the last two sections of the path
                //         const pathSplit = childPath.split(".");

                //         // pop last two items from pathSplit
                //         pathSplit.pop();
                //         pathSplit.pop();

                //         let pathSplitString = "";
                //         for (let j = 0; j < pathSplit.length; j++) {
                //             pathSplitString += pathSplit[j] + ".";
                //         }

                //         // create the new path for the new children
                //         let newPath = pathSplitString + (index + 1);
                //         console.log("2222222222 🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ newPath:", newPath)

                //         for (const key of Object.keys(part.securityGroupsFlat)) {
                //             if (part.securityGroupsFlat[key] === "Stats Manager") {
                //                 console.log("2222222222🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ key:", key)
                //                 console.log("2222222222🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ part.securityGroupsFlat[key]:", part.securityGroupsFlat[key])
                //                 console.log("2222222222🚀 ~  ", part.name, " createNewOrgTreeRelationshipsFlatObject ~ children:", children   );
                //             }

                //             const path = newPath + "." + key;
                //             const value = part.securityGroupsFlat[key];
                //             newFlat[path] = value;

                //         }

                //         // process any inherited securityGroups onto flat object
                //         if (part.inheritedSecurityGroupsFlat) {
                //             newPath = pathSplitString + (index + 2);
                //             for (const key of Object.keys(part.inheritedSecurityGroupsFlat)) {
                //                 const path = newPath + "." + key;
                //                 const value = part.inheritedSecurityGroupsFlat[key];
                //                 newFlat[path] = value;
                //             }
                //         }

                //         // process any users with securityGroups onto flat object
                //         if (part.positionUsersFlat) {
                //             newPath = pathSplitString + (index + 3);
                //             for (const key of Object.keys(part.positionUsersFlat)) {
                //                 const path = newPath + "." + key;
                //                 const value = part.positionUsersFlat[key];
                //                 newFlat[path] = value;
                //             }
                //         }
                //     }
                //     else {
                //         console.log("🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ children.length === 0:", children.length, part.securityGroupsArray.length);
                //         index = 0;
                //         const childPath = part.path;
                //         const pathSplit = childPath.split(".");

                //         // pop last item from pathSplit
                //         pathSplit.pop();

                //         let pathSplitString = "";
                //         for (let j = 0; j < pathSplit.length; j++) {
                //             pathSplitString += pathSplit[j] + ".";
                //         }

                //         // create the new path for the new children
                //         let newPath = pathSplitString + "children." + index;

                //         for (const key of Object.keys(part.securityGroupsFlat)) {
                //             const path = newPath + "." + key;
                //             const value = part.securityGroupsFlat[key];
                //             newFlat[path] = value;
                //         }

                //         // process any inherited securityGroups onto flat object
                //         if (part.inheritedSecurityGroupsFlat) {
                //             newPath = pathSplitString + (index + 2);
                //             for (const key of Object.keys(part.inheritedSecurityGroupsFlat)) {
                //                 const path = newPath + "." + key;
                //                 const value = part.inheritedSecurityGroupsFlat[key];
                //                 newFlat[path] = value;
                //             }
                //         }

                //         // process any users with securityGroups onto flat object
                //         if (part.positionUsersFlat) {
                //             newPath = pathSplitString + "children." + (index + 3);
                //             for (const key of Object.keys(part.positionUsersFlat)) {
                //                 const path = newPath + "." + key;
                //                 const value = part.positionUsersFlat[key];
                //                 newFlat[path] = value;
                //             }
                //         }
                //     }

                //     // // build the org positions tree flat object
                //     // if (children.length > 0) {
                //     //     console.log("🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ children.length > 0 second part ****:", children.length, part.securityGroupsArray.length);
                //     //     index = children.length - 1;
                //     //     childPath = children[0].path;
                //     //     // new path for the current parts new children to add
                //     //     // remove the last two sections of the path
                //     //     const pathSplit = childPath.split(".");

                //     //     // pop last two items from pathSplit
                //     //     pathSplit.pop();
                //     //     pathSplit.pop();

                //     //     let pathSplitString = "";
                //     //     for (let j = 0; j < pathSplit.length; j++) {
                //     //         pathSplitString += pathSplit[j] + ".";
                //     //     }

                //     //     // create the new path for the new children
                //     //     const newPath = pathSplitString + (index + 1);

                //     //     for (const key of Object.keys(part.securityGroupsFlat)) {
                //     //         const path = newPath + "." + key;
                //     //         const value = part.securityGroupsFlat[key];
                //     //         newFlat[path] = value;
                //     //     }
                //     // }
                // }

                // This section sets up the org positions tree flat object by adding the part and its children to the flat object using the paths to manipulate the paths
                // NOT the securityGroups, inherited securityGroups or users with securityGroups
                for (const key of Object.keys(part)) {
                    if (key !== 'levelUp') {
                        const path = part.path + '.' + key;
                        const value = part[key];
                        newFlat[path] = value;
                        // console.log("🚀 ~ createNewOrgTreeRelationshipsFlatObject levelUp ~ value:", value)

                        pathResult = path;
                    } else {
                        const path = part.path.substring(0, part.path.lastIndexOf('.')); // removes last item in path "item.item.xxx"

                        for (const key2 of Object.keys(part[key])) {
                            const pathResult = path === '' ? path + key2 : path + '.' + key2;
                            const value = part[key][key2];

                            newFlat[pathResult] = value;
                            // console.log("🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ value:", value)
                        }
                    }
                }
            }
            // console.log("🚀 ~ createNewOrgTreeRelationshipsFlatObject ~ newFlat:", newFlat)

            return newFlat;
        }

        // Remove any data node for "Org securityGroups on Position" and "Org Inherited securityGroups on Position".
        function removeChildrenSecurityTrees(node: any) {
            // console.log(" ****** ");
            if (node.children) {
                // console.log("🚀 ~ removeChildrenSecurityTrees ~ node.children:", node.data.name , " - ", node.children.length, " - ", node.children)

                let i = node.children.length;
                while (i--) {
                    const child = node.children[i];

                    if (
                        child?.data?.name?.includes('Org securityGroups on Position') ||
                        child?.data?.name?.includes('Org Inherited securityGroups on Position')
                    ) {
                        node.children = node.children.filter((o: any) => o.data.name !== child.data.name);
                    }
                }

                if (node?.children?.length > 0) {
                    node.children.forEach(removeChildrenSecurityTrees);
                }

                // console.log("🚀 ~ removeChildrenSecurityTrees ~ node.children filtered:", node.data.name , " - ", node.children.length, " - ", node.children)
            }
        }

        // console.log('buildPositionsTreeFromPart > unFlattened: ', unFlattened);
        return unFlattened;
    }

    public flattenPositions<T extends { children: any[] }>(positionsData: T) {
        function recursivelyFlattenArray(node: T, arr: T[]): any[] {
            arr.push(node);

            if (node.children) node.children.forEach((child: T) => recursivelyFlattenArray(child, arr));

            return arr;
        }

        return recursivelyFlattenArray(positionsData, []);
    }
}

function fromStringToHex(color: string): string {
    // Possible example of the objects that will call this function include:
    //     const partData = {
    //         linkColor: "blue",
    //         background: "blue",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "orange",
    //         background: "orange",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "purple",
    //         background: "purple",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "pink",
    //         background: "pink",
    //         color: "black",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "lime",
    //         background: "lime",
    //         color: "black",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "grey",
    //         background: "grey",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "yellow",
    //         background: "yellow",
    //         color: "black",
    //         selected: true,
    //     };
    // Taking this into consideration, we must return the 7 code HEX value of the color.
    // For example, if the color is "blue", we must return "#0000FF".

    switch (color) {
        case 'blue':
            return '#0000FF';
        case 'orange':
            return '#FFA500';
        case 'purple':
            return '#800080';
        case 'pink':
            return '#FFC0CB';
        case 'lime':
            return '#00FF00';
        case 'grey':
            return '#808080';
        case 'yellow':
            return '#FFFF00';
        case 'red':
            return '#FF0000';
        case 'green':
            return '#008000';
        case 'white':
            return '#FFFFFF';
        case 'black':
            return '#000000';
        default:
            return color;
    }
}

class Org {
    org: any; // Organization;
    positions: any;
    orgTree: any;
    orgUsers?: any;
    tree?: string;

    static withOrg(organization: Organization, orgTree?: any, t2?: string, orgUsers?: any) {
        // console.log(' >>> ', ' withOrg: ', t2);
        return new this(organization, orgTree, t2, orgUsers);
    }

    constructor(org: Organization, orgTree?: any, tree?: string, orgUsers?: any) {
        this.org = deepCopy(org);
        this.orgTree = deepCopy(orgTree);
        // console.log(' >>> ', ' constructor: ', tree);
        this.tree = deepCopy(tree);
        this.orgUsers = deepCopy(orgUsers);
    }

    public run() {
        // do everything in intent list - all updates to org doc and sub-collection docs
        return this.org;
    }

    public processChanges() {
        // do everything in intent list - all updates to org doc and sub-collection docs
        return this.org;
    }

    /**
     *
     * @param {string} tree
     * @return {*}
     * @memberof OrgTree
     */
    public newInstance(tree: string) {
        // console.log(' >>> ', ' newInstance: ', tree);
        return new Org(this.org, this.orgTree, tree, this.orgUsers);
    }

    /**
     * This method adds a sg to a user in the org. It does not add the sg to the org.
     *
     * @param {*} securityGroup - sg to add to the org
     * @param {*} user - user to add the sg to
     * @return {*}
     * @memberof OrgTree
     * @example ```typescript
     * const orgTree = OrgTree.withOrg(org,"","tree1").addSecurityGroupToUser(securityGroup1,user1).log();
     * ```
     */
    public addSecurityGroupToUser(securityGroup: any, user: any) {
        // generate orgTree from parts
        for (let i = 0; i < this.org.orgParts.length; i++) {
            const part = this.org.orgParts[i];
        }
        // find user in this.org
        const userFound = this.org.orgUsers.find((u: any) => u.key === user.key);
        // find the securityGroup in this.org
        const sgFound = this.org.orgSecurityGroups.find((h: any) => h.key === securityGroup.key);

        let message = '';
        if (userFound && sgFound) {
            // add securityGroup to user
            securityGroup.assigned = true;
            securityGroup.assignedTo = user.key;
            userFound.securityGroupDetails[securityGroup.key] = securityGroup;
            userFound.features = [...userFound.features, ...securityGroup.features];
            userFound.featureDetails = { ...userFound.featureDetails, ...securityGroup.featureDetails };
            this.org.orgUsers = [...this.org.orgUsers.filter((u: any) => u.key !== user.key), userFound];
            this.org.orgSecurityGroups = [
                ...this.org.orgSecurityGroups.filter((h: any) => h.key !== securityGroup.key),
                securityGroup,
            ];
        } else {
            message = 'user or security group not found';
            // can handle errors here
        }

        // console.log(' message >>> ', message);
        return this;
    }

    public addSecurityGroupToOrg(securityGroup: any) {
        this.org.orgSecurityGroups.push(securityGroup);
        return this;
    }

    public addUserToOrg(user: any) {
        this.org.orgUsers.push(user);
        return this;
    }

    public log() {
        // console.log(' >>> ', ' tree: ', this.tree);
        // console.log(' >>> ', ' tree: ', this.tree, ' : ', this.org);
        // console.log(' >>> ', ' orgTree: ', this.orgTree);
        return this;
    }

    // public writeFile(filename: string) {
    //     fs.writeFileSync(path.join(__dirname, `${filename}.json`), JSON.stringify(this.run()));
    //     return this;
    // }
}

export class OrgTree extends Org {
    override org: any;
    override positions: Position[] = [];
    override orgTree: any;
    override orgUsers?: any;
    override tree?: string;

    private partsSwatch: any = {
        org: {
            color: 'white',
            background: 'red',
        },
        securityGroup: {
            color: 'white',
            background: 'green',
        },
        feature: {
            color: 'white',
            background: 'blue',
        },
        featureDetails: {
            color: 'white',
            background: '#6060f4',
        },
        permission: {
            color: 'white',
            background: '#6060f4',
        },
        role: {
            color: 'white',
            background: 'yellow',
        },
        user: {
            color: 'white',
            background: '#c2185b',
        },
        orgSecurityGroupsSubCollection: {
            color: 'black',
            background: '#04d704',
        },
        userOnOtherSecurityGroupsWithSamePosition: {
            color: 'white',
            background: '#4d1c2f',
        },
        securityGroupsOnPositionObject: {
            color: 'black',
            background: '#04d704',
        },
    };
    private securityGroupObject: any = null;
    private securityGroupsTreeObjectForOrgSecurityGroups: any = {}; // All the sgs for the selected org derived from org doc - orgSecurityGroups object
    public treesMap: Record<string, any> = {};

    /**
     * This static method creates a new instance of the OrgTree class.
     *
     * @static
     * @memberof OrgTree
     */
    static override withOrg(v: any, t?: any, t2?: string, orgUsers?: any, isInitial = false) {
        // console.log(' >>> ', ' withOrg: ', t2);
        return new this(v, t, t2, orgUsers, isInitial);
    }

    constructor(org: any, orgTree?: any, tree?: string, orgUsers?: any, isInitial = false) {
        super(deepCopy(org), deepCopy(orgTree), deepCopy(tree));
        // console.log(' >>> ', ' constructor: ', tree);

        if (isInitial) {
            this.positions = this.initial(this.tree || '').positions;
            // // console.log("this.positions: ", this.positions);
        }

        this.org = deepCopy(org);
        this.orgTree = deepCopy(orgTree);
        this.tree = deepCopy(tree);
        this.orgUsers = deepCopy(orgUsers);
    }

    /**
     *
     * @param {string} tree
     * @return {*}
     * @memberof OrgTree
     */
    override newInstance(tree: string) {
        // console.log(' >>> ', ' newInstance: ', tree);
        return new OrgTree(this.org, this.orgTree, tree, this.orgUsers);
    }

    private initial(tree: string) {
        // do everything in intent list - all updates to org doc and sub-collection docs
        this.orgTree = { tree: tree };

        const orgParts = this.org.orgParts;

        const positionsWillInheritFromSecurityGroupAbove2 = this.appendAllChildPositionsToOrgParts(orgParts); // This builds the rudimentary tree structure of the organization, which can be used for various purposes.

        // console.log("positionsWillInheritFromSecurityGroupAbove2 : ", positionsWillInheritFromSecurityGroupAbove2);
        if (!orgParts || !orgParts[0]) {
            return this;
        }

        // find the first section of the path in partToStartFrom and identify the substring section to remove
        const partToStartFromPath = deepCopy(orgParts[0].path);
        if (!partToStartFromPath) {
            console.warn('OrgTree.initial: orgParts[0].path is undefined, skipping tree build', {
                partKey: orgParts[0].partKey,
                name: orgParts[0].name,
            });
            return this;
        }
        const partToStartFromPathSplit = partToStartFromPath.split('.');

        let partToStartFromPathSplitString = '';
        for (let i = 0; i < partToStartFromPathSplit.length - 1; i++) {
            partToStartFromPathSplitString += partToStartFromPathSplit[i] + '.';
        }

        // process the parts/positions that will inherit from the sg above
        const positionsCopy = deepCopy(positionsWillInheritFromSecurityGroupAbove2);

        // console.log("🚀 ~ OrgTree ~ initial ~ positionsWillInheritFromSecurityGroupAbove2:", JSON.stringify(positionsCopy[0]));

        let positions = this.buildObjectForAllSecurityGroupsForPositions(
            positionsCopy,
            partToStartFromPathSplitString,
            this.org,
            this.org.key,
            this.tree || 'tree5'
        );
        // let positions = positionsCopy;

        this.extractSecurityGroupsForInheritedPositions(positions);
        const userObjectArray: any = this.extractUserDetailsForPositions(positions);

        const userObjectArrayCopy = deepCopy(userObjectArray);

        // *****************************************************
        // ***** add the user details to the parts         *****
        // *****************************************************
        positions = this.appendUserDetailsToParts(userObjectArrayCopy, positions, this.orgUsers); //?
        // console.log("positions 1: ", positions);

        this.updateHeldFromAboveMapping(positions);
        // console.log("positions 1: ", positions);

        // *******************************************************************************
        // ***** attach all sections & units details to the department parts         *****
        // *******************************************************************************
        positions = this.appendSectionsAndUnitsDetailsToParts(userObjectArrayCopy, positions, this.orgUsers); //?
        // console.log("positions 2: ", positions);

        // const filename = "positions";
        // fs.writeFileSync(path.join(__dirname, `${filename}.json`), JSON.stringify(positions));
        // console.log("*#*#*#*#*#*#*#*# positions : ", positions);
        this.positions = positions;

        return this;
    }

    // Container part types — structural nodes that can NEVER be directly staffed.
    // Only Posts can receive users and participate in HFA.
    private static readonly CONTAINER_PART_TYPES = new Set(['executive', 'division', 'department', 'section', 'unit']);

    private updateHeldFromAboveMapping(positions: any) {
        for (let i = 0; i < positions.length; i++) {
            const node: Position = positions[i];
            const nodePartType = (node as any).partType as string;

            // Always clear existing HFA data first
            if (node.heldFromAboveUserName && node.heldFromAboveUid) {
                node.heldFromAboveUid = undefined;
                node.heldFromAboveUserName = undefined;
                node.heldFromAboveColor = undefined;
                node.heldFromAbovePartKey = undefined;
            }
            node.pendingHfaUserDetailsArray = undefined;

            // Containers (executive/division/department/section/unit) are structural only.
            // They cannot be staffed and do not receive HFA. Only Posts participate in HFA.
            if (OrgTree.CONTAINER_PART_TYPES.has(nodePartType)) {
                continue;
            }

            // For HFA, only consider ACTIVE (non-pending) users as "occupying" a post
            const activeUsers = node.userDetailsArray?.filter((u: any) => !u?.isPending) || [];

            if (activeUsers.length > 0) {
                // Post has active users — no HFA needed
                continue;
            }

            // Post has no active users (may have pending users or be truly vacant)
            // Look UP parent hierarchy for HFA — only use ACTIVE users as HFA source
            // Pending users are never HFA sources; they only show on their own post in edit mode
            {
                const parentFamilyTree = this.getParentAndGrandParents(node, positions);

                let hfaUIDs: string[] = [];
                const hfaNames: string[] = [];
                let hfaColor = '';
                let hfaPartKey = '';
                let depthOfHFA = -1;

                // The node's direct parent container key — posts under this container are
                // siblings, not ancestors, and must be excluded from HFA sources.
                const nodeParentPartKey = (node as any).parentPartKey;

                for (let j = 0; j < parentFamilyTree.length; j++) {
                    const p = parentFamilyTree[j];
                    const pPartType = (p as any).partType as string;

                    if (!~p.id) continue; // If ID is -1, skip.

                    // Containers don't have users directly, but their child Posts may.
                    if (OrgTree.CONTAINER_PART_TYPES.has(pPartType)) {
                        // Skip the node's own parent container — its child posts are siblings,
                        // not a higher level. HFA only comes from ABOVE, not from the same level.
                        if (p.partKey === nodeParentPartKey) {
                            continue;
                        }

                        const childPosts = positions.filter(
                            // `excludeFromHfa` posts (e.g. a Board-of-Directors member who
                            // governs but does not "run" the org) are NOT HFA sources, so
                            // they don't cascade their occupant onto every position below.
                            (cp: any) => cp.partType === 'post' && cp.parentPartKey === p.partKey && !cp.excludeFromHfa
                        );
                        for (const childPost of childPosts) {
                            // Only use ACTIVE users as HFA source
                            const childActiveUsers =
                                childPost.userDetailsArray?.filter((u: any) => !u?.isPending) || [];
                            if (childActiveUsers.length > 0) {
                                if (~depthOfHFA && p.depth !== depthOfHFA) break;
                                hfaUIDs = hfaUIDs.concat(childActiveUsers.map((x: any) => x.key)).flat();
                                hfaNames.push(childActiveUsers.map((x: any) => x.name).join(', '));
                                if (!hfaColor && p.colour?.background) hfaColor = p.colour.background;
                                if (!hfaPartKey && (childPost.partKey || childPost.key))
                                    hfaPartKey = childPost.partKey || childPost.key;
                                if (depthOfHFA === -1 && ~p.depth) depthOfHFA = p.depth;
                            }
                        }
                        continue;
                    }

                    if (~depthOfHFA && p.depth !== depthOfHFA) break;

                    // Only use ACTIVE users from parent posts as HFA
                    const parentActiveUsers = p.userDetailsArray?.filter((u: any) => !u?.isPending) || [];
                    if (parentActiveUsers.length > 0) {
                        hfaUIDs = hfaUIDs.concat(parentActiveUsers.map((x: any) => x.key)).flat();
                        hfaNames.push(parentActiveUsers.map((x: any) => x.name).join(', '));
                        if (!hfaColor && p.colour?.background) hfaColor = p.colour.background;
                        if (!hfaPartKey && p.partKey) hfaPartKey = p.partKey;
                        if (depthOfHFA === -1 && ~p.depth) depthOfHFA = p.depth;
                    } else {
                        continue;
                    }
                }

                if (hfaUIDs.length > 0) {
                    node.heldFromAboveUid = hfaUIDs.join(',') as string;
                    node.heldFromAboveUserName = hfaNames.join(', ') as string;
                    node.heldFromAboveColor = hfaColor || undefined;
                    node.heldFromAbovePartKey = hfaPartKey || undefined;
                }
            }

            // DISABLED: Pending HFA propagation (April 2026)
            // Pending users should only appear on the specific post they're pending on,
            // not as HFA on sibling or child posts. A user awaiting approval does not
            // "hold" any position yet.
            //
            // FUTURE: If we want to show pending HFA (e.g., greyed out HFA indicator in
            // edit mode), re-enable this block but ensure it also skips the node's direct
            // parent container (nodeParentPartKey) to avoid sibling contamination, just
            // like the active HFA pass above.

            // // SECOND PASS: Find pending HFA — nearest ancestor with PENDING users
            // // Used in edit mode to show pending users as HFA on child posts
            // {
            //     const parentFamilyTree = this.getParentAndGrandParents(node, positions);
            //     const pendingHfaUsers: UserDetails[] = [];

            //     for (let j = 0; j < parentFamilyTree.length; j++) {
            //         const p = parentFamilyTree[j];
            //         const pPartType = (p as any).partType as string;

            //         if (!~p.id) continue;

            //         if (OrgTree.CONTAINER_PART_TYPES.has(pPartType)) {
            //             const childPosts = positions.filter(
            //                 (cp: any) => cp.partType === 'post' && cp.parentPartKey === p.partKey
            //             );
            //             for (const childPost of childPosts) {
            //                 const childPendingUsers = childPost.userDetailsArray?.filter((u: any) => u?.isPending) || [];
            //                 if (childPendingUsers.length > 0) {
            //                     pendingHfaUsers.push(...childPendingUsers);
            //                 }
            //             }
            //             if (pendingHfaUsers.length > 0) break;
            //             continue;
            //         }

            //         const parentPendingUsers = p.userDetailsArray?.filter((u: any) => u?.isPending) || [];
            //         if (parentPendingUsers.length > 0) {
            //             pendingHfaUsers.push(...parentPendingUsers);
            //             break;
            //         }
            //     }

            //     if (pendingHfaUsers.length > 0) {
            //         node.pendingHfaUserDetailsArray = pendingHfaUsers;
            //     }
            // }
        }

        // Linked posts (e.g. Dept 19's "Office of the CFO") show their target post's holders —
        // after every post's own HFA is known, so a vacant target can lend its HFA.
        applyLinkedPosts(positions.filter((p: any) => p?.partType === 'post'));
    }

    /**
     * Recursively gets all parents of the node, and each of the parent's parents.
     */
    private getParentAndGrandParents(node: any, positions: Position[], nodes: any[] = []) {
        if (node.parentPartKey) {
            // If the node has a parent, find in posisions with matching partKey to parentPartKey and add it to the nodes array.
            const parentNode = positions.find((p: Position) => p.partKey === node.parentPartKey);
            if (parentNode) {
                nodes = [...nodes, parentNode];
            } else {
                // If no parent node found, return the nodes array.
                return nodes;
            }

            nodes = this.getParentAndGrandParents(parentNode, positions, nodes);
        }
        return nodes;
    }

    buildTree(tree: string) {
        return this;
    }

    buildTreeObjectForAllSecurityGroupsForAllPositions(tree: string) {
        for (let i = 0; i < this.positions.length; i++) {
            const securityGroupObjectArray2: any = [];
            for (const sg of this.positions[i].securityGroupsArray) {
                const orgSecurityGroupsSubColFeatureObject = [];

                for (const featureKey in sg.featureDetails) {
                    const feature = sg.featureDetails[featureKey];

                    const featureObject: any = {
                        selected: true,
                        color: this.fetchColor('feature'),
                        background: this.fetchBackground('feature'),
                        data: {
                            depth: 2,
                            key: featureKey,
                            expandedKey:
                                tree +
                                '-' +
                                this.org.key +
                                '-' +
                                '-' +
                                sg.key +
                                '-' +
                                featureKey +
                                '-orgSecurityGroupsSubCol',
                            name: featureKey,
                            type: 'feature',
                        },
                        children: [
                            {
                                selected: true,
                                color: this.fetchColor('featureDetails'),
                                background: this.fetchBackground('featureDetails'),
                                data: {
                                    depth: 2,
                                    key: featureKey,
                                    expandedKey:
                                        tree +
                                        '-' +
                                        this.org.key +
                                        '-' +
                                        '-' +
                                        sg.key +
                                        '-' +
                                        featureKey +
                                        '-featureDetails' +
                                        '-orgSecurityGroupsSubCol',
                                    name: feature.permissions,
                                    type: 'featureDetails',
                                },
                                children: [],
                            },
                        ],
                    };
                    orgSecurityGroupsSubColFeatureObject.push(featureObject);
                }

                if (orgSecurityGroupsSubColFeatureObject.length > 0) {
                    const securityGroupObject: any = {
                        selected: true,
                        color: this.fetchColor('securityGroup'),
                        background: this.fetchBackground('securityGroup'),
                        data: {
                            depth: 2,
                            key: sg.key,
                            expandedKey: tree + '-' + this.org.key + '-' + '-' + sg.key,
                            name: sg.name,
                            multiUsers: sg.multiUsers,
                            type: 'securityGroup',
                        },
                        children: orgSecurityGroupsSubColFeatureObject,
                    };

                    securityGroupObjectArray2.push(securityGroupObject);
                }
            }

            if (securityGroupObjectArray2.length > 0) {
                const securityGroupsOnPositionObject: any = {
                    selected: true,
                    color: this.fetchColor('securityGroupsOnPositionObject'),
                    background: this.fetchBackground('securityGroupsOnPositionObject'),
                    data: {
                        depth: 2,
                        key: this.positions[i].name,
                        expandedKey: tree + '-' + this.org.key + '-securityGroupOnPositionObject',
                        name: 'Org Security Groups on Position: ** ' + this.positions[i].name + ' **',
                        type: 'securityGroups',
                    },
                    children: securityGroupObjectArray2,
                };

                const flattenSecurityGroupsObject = flatten(securityGroupsOnPositionObject);

                this.positions[i].securityGroupsFlat = flattenSecurityGroupsObject;

                this.positions[i].securityGroupsObject = securityGroupsOnPositionObject;
            } else {
                this.positions[i].securityGroupsFlat = {};

                this.positions[i].securityGroupsObject = {};
            }
        }
        return this;
    }

    getUserDetailsForPosition(uid: any, position: any) {
        const positionFound = this.positions.find((u: any) => u.key === position);
        if (positionFound) {
            const userFound = positionFound.userDetailsArray.find((u: any) => u.key === uid);
            if (userFound) {
                const positionsObjects = userFound.positionsObjects[position];
                return positionsObjects;
            }
        }

        return {};
    }

    getUserPrimarySecurityGroupsForPosition(uid: any, position: any) {
        const positionFound = this.positions.find((u: any) => u.key === position);
        if (positionFound) {
            const userFound = positionFound.userDetailsArray.find((u: any) => u.key === uid);
            if (userFound) {
                const positionsObjects = userFound.positionsObjects[position];
                const primarySecurityGroups = positionsObjects.primaryPositionSecurityGroups;
                return primarySecurityGroups;
            }
        }
        return [];
    }

    getUserSecondarySecurityGroupForPosition(uid: any, position: any) {
        const positionFound = this.positions.find((u: any) => u.key === position);
        if (positionFound) {
            const userFound = positionFound.userDetailsArray.find((u: any) => u.key === uid);
            if (userFound) {
                const positionsObjects = userFound.positionsObjects[position];
                const primarySecurityGroups = positionsObjects.secondaryPositionsSecurityGroups;
                return primarySecurityGroups;
            }
        }
        return [];
    }

    getUserPositions(uid: any) {
        const userPositions: any = [];
        const positions = this.positions;
        for (const position of positions) {
            const userFound = position.userDetailsArray
                ? position.userDetailsArray.find((u: any) => u.key === uid)
                : undefined;
            if (userFound) {
                userPositions.push(userFound.positionsObjects);
            }
        }

        return userPositions;
    }

    getUserFeatureDetailsForPosition(uid: any, departmentKey: any) {
        const userPositions: any = [];
        const positions = this.positions;
        for (const position of positions) {
            const userFound = position.userDetailsArray
                ? position.userDetailsArray.find((u: any) => u.key === uid)
                : undefined;
            if (userFound) {
                userPositions.push(userFound.positionsObjects);
            }
        }

        return userPositions[0][departmentKey] !== undefined ? userPositions[0][departmentKey].featureDetails : {};
    }

    getUserFeatureDetailsForAllPositions(uid: any) {
        const userPositions: any = [];
        const positions = this.positions;
        for (const position of positions) {
            const userFound = position.userDetailsArray
                ? position.userDetailsArray.find((u: any) => u.key === uid)
                : undefined;
            if (userFound) {
                userPositions.push(userFound.positionsObjects);
            }
        }

        // console.log("userPositions : ", userPositions);

        const featuresAndPermissionMerged: any = {};
        for (const positionKey in userPositions[0]) {
            // console.log("positionKey : ", positionKey);
            const position = userPositions[0][positionKey]; //?
            // // console.log("position : ", position); //?
            const positionsFeatureDetails = position.featureDetails;
            // console.log("positionsFeatureDetails : ", positionsFeatureDetails);
            for (const featureKey in positionsFeatureDetails) {
                if (featuresAndPermissionMerged[featureKey] === undefined) {
                    // console.log(positionsFeatureDetails[featureKey].permissions);
                    featuresAndPermissionMerged[featureKey] = {
                        permissions: [...positionsFeatureDetails[featureKey].permissions],
                    };
                } else {
                    featuresAndPermissionMerged[featureKey].permissions = [
                        ...new Set([
                            ...featuresAndPermissionMerged[featureKey].permissions,
                            ...positionsFeatureDetails[featureKey].permissions,
                        ]),
                    ];
                }
            }
            // const mergedFeatureDetailsOfPrimeAndInherited = {
            //     permissions: [...new Set([...inheritedFeatureDetail.permissions])],
            //     name: inheritedFeatureDetail.name,
            //     key: inheritedFeatureDetail.key
            // };
        }

        return featuresAndPermissionMerged; // userPositions[0][departmentKey].featureDetails;
    }

    override processChanges() {
        // do everything in intent list - all updates to org doc and sub-collection docs
        return this.orgTree;
    }

    override log() {
        // // console.log(' >>> ', ' tree: ', this.tree);
        // // console.log(' >>> ', ' orgTree: ', this.orgTree);

        return this;
    }

    fetchColor(type: string) {
        const color = this.partsSwatch[type].color;
        return color;
    }

    fetchBackground(type: string) {
        const background = this.partsSwatch[type].background;
        return background;
    }

    setSecurityGroupsTreeObject(childObjectArray: any, org: any, sg: any, tree: string) {
        if (this.securityGroupObject === null || this.securityGroupObject === undefined) {
            this.securityGroupObject = {
                selected: true,
                color: this.fetchColor('securityGroup'),
                background: this.fetchBackground('securityGroup'),
                data: {
                    depth: 1,
                    key: sg.key,
                    expandedKey: tree + '-' + org.key + '-' + sg.key,
                    name: sg.name,
                    multiUsers: sg.multiUsers,
                    type: 'securityGroup',
                    isChecked: false,
                },
                children: [],
            };
        }

        this.securityGroupObject.children.push(childObjectArray);

        return this.securityGroupObject;
    }

    buildTreeObjectForAllFeaturesAndPermissionsOfSecurityGroup(org: any, sg: any, tree: string) {
        const orgKey = org.key;
        const securityGroupObjectArray: any = [];
        // // console.log('org >>>>>: ', org);
        // // console.log('sg >>>>>: ', sg);
        for (const SGKey of org.orgSecurityGroups.securityGroupsKeys) {
            const securityGroupFeatureObjectArray: any = [];
            const foundSg = org.orgSecurityGroups[SGKey].key === sg.key ? org.orgSecurityGroups[SGKey] : undefined;

            // // console.log('foundSg >>>>>: ', foundSg);
            if (foundSg) {
                for (const featureKey of foundSg.features) {
                    const featureDetail = foundSg.featureDetails[featureKey];
                    const featureObject: any = {
                        selected: true,
                        color: this.fetchColor('feature'),
                        background: this.fetchBackground('feature'),
                        data: {
                            depth: 2,
                            key: featureKey,
                            expandedKey: tree + '-' + orgKey + '-' + foundSg.key + '-' + SGKey + '-' + featureKey,
                            name: featureKey,
                            type: 'feature',
                            isChecked: false,
                        },
                        children: [
                            {
                                selected: true,
                                color: this.fetchColor('featureDetails'),
                                background: this.fetchBackground('featureDetails'),
                                data: {
                                    depth: 2,
                                    key: 'featureDetails-' + featureKey,
                                    expandedKey:
                                        tree +
                                        '-' +
                                        orgKey +
                                        '-' +
                                        foundSg.key +
                                        '-' +
                                        'featureDetails-' +
                                        SGKey +
                                        '-' +
                                        featureKey,
                                    name: featureDetail.permissions,
                                    type: 'featureDetails',
                                    isChecked: false,
                                },
                            },
                        ],
                    };
                    securityGroupFeatureObjectArray.push(featureObject);
                }
                const sgObject: any = {
                    selected: true,
                    color: this.fetchColor('securityGroup'),
                    background: this.fetchBackground('securityGroup'),
                    data: {
                        depth: 2,
                        key: sg.key,
                        expandedKey: tree + '-' + orgKey + '-' + foundSg.key + '-orgSecurityGroups-' + sg.key,
                        name: sg.name,
                        multiUsers: sg.multiUsers,
                        type: 'securityGroup',
                        isChecked: false,
                    },
                    children: securityGroupFeatureObjectArray,
                };
                securityGroupObjectArray.push(sgObject);
            }
        }

        // All the sgs for the selected position derived from org doc - orgSecurityGroups object
        const securityGroupsForOrgSecurityGroupsObject: any = {
            selected: true,
            color: this.fetchColor('securityGroup'),
            background: this.fetchBackground('securityGroup'),
            data: {
                depth: 2,
                key: sg.key + '-' + org.key + '-orgSecurityGroups',
                expandedKey: tree + '-' + orgKey + '-' + sg.key + '-orgSecurityGroups',
                name: 'orgSecurityGroups for ' + sg.key,
                type: 'securityGroups',
                isChecked: false,
            },
            children: securityGroupObjectArray,
        };

        return securityGroupsForOrgSecurityGroupsObject;
    }

    buildTreeObjectForAllFeaturesAndPermissionsOfSecurityGroup2(
        orgSecurityGroupsSubColObject: any,
        org: any,
        securityGroup: any,
        tree: string
    ) {
        const securityGroupObjectArray2: any = [];
        for (const sg of orgSecurityGroupsSubColObject) {
            const orgSecurityGroupsSubColFeatureObject = [];

            for (const featureKey in sg.featureDetails) {
                const feature = sg.featureDetails[featureKey];

                const featureObject: any = {
                    selected: true,
                    color: this.fetchColor('feature'),
                    background: this.fetchBackground('feature'),
                    data: {
                        depth: 2,
                        key: featureKey,
                        expandedKey:
                            tree + '-' + org.key + '-' + '-' + sg.key + '-' + featureKey + '-orgSecurityGroupsSubCol',
                        name: featureKey,
                        type: 'feature',
                        isChecked: false,
                    },
                    children: [
                        {
                            selected: true,
                            color: this.fetchColor('featureDetails'),
                            background: this.fetchBackground('featureDetails'),
                            data: {
                                depth: 2,
                                key: featureKey,
                                expandedKey:
                                    tree +
                                    '-' +
                                    org.key +
                                    '-' +
                                    '-' +
                                    sg.key +
                                    '-' +
                                    featureKey +
                                    '-featureDetails' +
                                    '-orgSecurityGroupsSubCol',
                                name: feature.permissions,
                                type: 'featureDetails',
                                isChecked: false,
                            },
                            children: [],
                        },
                    ],
                };
                orgSecurityGroupsSubColFeatureObject.push(featureObject);
            }

            const sgObject: any = {
                selected: true,
                color: this.fetchColor('securityGroup'),
                background: this.fetchBackground('securityGroup'),
                data: {
                    depth: 2,
                    key: sg.key,
                    expandedKey: tree + '-' + org.key + '-' + '-' + sg.key,
                    name: sg.name,
                    multiUsers: sg.multiUsers,
                    type: 'securityGroup',
                    isChecked: false,
                },
                children: orgSecurityGroupsSubColFeatureObject,
            };
            securityGroupObjectArray2.push(sgObject);
        }

        // All the sgs for the selected position derived from org sgs sub-collection
        const orgSecurityGroupsSubCollectionsObject: any = {
            selected: true,
            color: this.fetchColor('orgSecurityGroupsSubCollection'),
            background: this.fetchBackground('orgSecurityGroupsSubCollection'),
            data: {
                depth: 2,
                key: securityGroup.key,
                expandedKey: tree + '-' + org.key + '-' + securityGroup.key + '-orgSecurityGroupsSubCol',
                name: 'Org Security Groups Sub-Collections ' + securityGroup.key,
                type: 'orgSecurityGroupsSubCollection',
                isChecked: false,
            },
            children: securityGroupObjectArray2,
        };

        return orgSecurityGroupsSubCollectionsObject;
    }

    extractAllOrgUsersForPosition(userDetails: any, org: any, sg: any, tree: string) {
        const userKeysOnSecurityGroup: any = [];
        if (sg.multiUsers && sg.multiUsersArray > 0) {
            userKeysOnSecurityGroup.push(...sg.multiUsersArray);
        } else {
            userKeysOnSecurityGroup.push(sg.assignedTo);
        }

        const userDetailsArray: any = [];
        for (const userKey of userKeysOnSecurityGroup) {
            // get user details and create user object
            const user = userDetails.users.find((o: any) => {
                if (o.uid === userKey) {
                    return o;
                }
            });

            const userDetailsObject: any = {
                selected: true,
                color: this.fetchColor('user'),
                background: this.fetchBackground('user'),
                data: {
                    depth: 1,
                    key: userKey,
                    expandedKey: tree + '-' + org.key + '-' + sg.key + '-' + userKey,
                    name: user?.name || userKey,
                    type: 'user',
                    isChecked: false,
                },
                children: [],
            };

            userDetailsArray.push(userDetailsObject);
        }

        const usersOnSecurityGroup: any = {
            selected: true,
            color: this.fetchColor('user'),
            background: this.fetchBackground('user'),
            data: {
                depth: 1,
                key: 'userOnSecurityGroup: ' + sg.key,
                expandedKey: tree + '-' + org.key + '-' + sg.key + 'userOnSecurityGroup',
                name: 'Users on security group: ' + sg.key,
                type: 'usersOnSecurityGroup',
                isChecked: false,
            },
            children: userDetailsArray,
        };

        return usersOnSecurityGroup;
    }

    extractUsersWithSamePositionOnOtherSecurityGroups(userDetails: any, org: any, sg: any, tree: string) {
        // Find other sgs in sgs sub collections with same position as selected sg
        const securityGroupsWithSamePositionArray: any = this.getSecurityGroupsWithSamePosition(org, sg);

        const securityGroupsWithSamePositionObjectArray: any = [];
        for (const securityGroupWithPosition of securityGroupsWithSamePositionArray) {
            // get user keys from the securityGroupWithPosition
            const userKeysOnSecurityGroup: any = [];
            if (securityGroupWithPosition.multiUsers && securityGroupWithPosition.multiUsersArray > 0) {
                userKeysOnSecurityGroup.push(...securityGroupWithPosition.multiUsersArray);
            } else {
                userKeysOnSecurityGroup.push(securityGroupWithPosition.assignedTo);
            }

            // create user objects
            const userDetailsArray: any = [];
            for (const userKey of userKeysOnSecurityGroup) {
                // get user details and create user object
                const user = userDetails.users.find((o: any) => {
                    if (o.uid === userKey) {
                        return o;
                    }
                });
                // // console.log("user *********************: ", user);

                const userDetailsObject: any = {
                    selected: true,
                    color: this.fetchColor('user'),
                    background: this.fetchBackground('user'),
                    data: {
                        depth: 1,
                        key: userKey,
                        expandedKey: tree + '-' + org.key + '-' + securityGroupWithPosition.key + '-' + userKey,
                        name: user?.name || userKey,
                        type: 'user',
                        isChecked: false,
                    },
                    children: [],
                };

                userDetailsArray.push(userDetailsObject);
            }

            const sgObject: any = {
                selected: true,
                color: this.fetchColor('securityGroup'),
                background: this.fetchBackground('securityGroup'),
                data: {
                    depth: 1,
                    key: securityGroupWithPosition.key,
                    expandedKey: tree + '-' + org.key + '-' + securityGroupWithPosition.key,
                    name: securityGroupWithPosition.name,
                    type: 'securityGroup',
                    isChecked: false,
                },
                children: userDetailsArray,
            };
            securityGroupsWithSamePositionObjectArray.push(sgObject);
        }

        const userOnOtherSecurityGroupsWithSamePosition: any = {
            selected: true,
            color: this.fetchColor('userOnOtherSecurityGroupsWithSamePosition'),
            background: this.fetchBackground('userOnOtherSecurityGroupsWithSamePosition'),
            data: {
                depth: 1,
                key: 'userOnOtherSecurityGroupsWithSamePosition: ' + sg.key,
                expandedKey: tree + '-' + org.key + '-' + sg.key + 'userOnOtherSecurityGroupsWithSamePosition',
                name: 'Users on Other Security Groups with same Position: ' + sg.key,
                type: 'userOnOtherSecurityGroupsWithSamePosition',
            },
            children: securityGroupsWithSamePositionObjectArray,
        };

        return userOnOtherSecurityGroupsWithSamePosition;
    }

    buildObjectForAllSecurityGroupsForPositions(
        positionsCopy: any,
        partToStartFromPathSplitString: string,
        org: any,
        orgKey: any,
        tree: string
    ) {
        for (let i = 0; i < positionsCopy.length; i++) {
            const path = positionsCopy[i].path;
            const name = positionsCopy[i].name;

            let pathSplitString = '';

            pathSplitString = path.substring(partToStartFromPathSplitString.length, path.length); //?

            const partData = {
                background: '#007bff',
                color: 'white',
                selected: true,
            };
            positionsCopy[i].levelUp = partData;
            positionsCopy[i].path = pathSplitString;
            positionsCopy[i].type = 'positionsPartsRelationship';
            positionsCopy[i].expandedKey = 'tree5-' + 'positionsPartsRelationship-' + pathSplitString;

            const securityGroupsForPosition: any = [];
            for (const SGKey of org.orgSecurityGroups.securityGroupsKeys) {
                const sgSearch = org.orgSecurityGroups[SGKey];
                // Defensive guard: an SG without a linked orgPosition (e.g. wizard-created
                // CEO SG before the post→position wiring runs) would crash the board page
                // with `Cannot read properties of undefined (reading 'partKey')`.
                if (sgSearch?.orgPosition?.partKey === positionsCopy[i].partKey) {
                    securityGroupsForPosition.push(sgSearch);
                }
            }
            positionsCopy[i].securityGroupArray = securityGroupsForPosition;

            // New logic: Amalgamate security groups from org.orgHats
            const amalgamatedSecurityGroupsFromHatsSet = new Set<any>();
            if (org.orgHats && org.orgHats.hatsKeys && Array.isArray(org.orgHats.hatsKeys)) {
                for (const hatKey of org.orgHats.hatsKeys) {
                    const currentHat = org.orgHats[hatKey];
                    if (currentHat && currentHat.positionArray && Array.isArray(currentHat.positionArray)) {
                        // Check if any position object in currentHat.positionArray has a partKey matching the current position's partKey
                        const hatMatchesPosition = currentHat.positionArray.some(
                            (partKey: any) => partKey && partKey === positionsCopy[i].partKey
                        );

                        if (hatMatchesPosition) {
                            if (currentHat.securityGroupsArray && Array.isArray(currentHat.securityGroupsArray)) {
                                currentHat.securityGroupsArray.forEach((sgFromHat: any) => {
                                    amalgamatedSecurityGroupsFromHatsSet.add(sgFromHat); // Add the SG object/reference from the hat
                                });
                            }
                        }
                    }
                }
            }

            // Resolve the SG objects/references from the set to their canonical versions from org.orgSecurityGroups
            const resolvedAmalgamatedSGsFromHats: any[] = [];
            if (org.orgSecurityGroups) {
                // Ensure org.orgSecurityGroups itself exists
                for (const sgObjectFromHat of amalgamatedSecurityGroupsFromHatsSet) {
                    // Assuming sgObjectFromHat is an object and has a 'key' property that is a string
                    if (sgObjectFromHat && typeof sgObjectFromHat.key === 'string') {
                        const sgKey = sgObjectFromHat.key;
                        const canonicalSgObject = org.orgSecurityGroups[sgKey]; // Look up in the main SG map
                        if (canonicalSgObject) {
                            resolvedAmalgamatedSGsFromHats.push(canonicalSgObject);
                        } else {
                            // Optionally log if a key from a hat's SG object doesn't find a match
                            // console.warn(`Security Group with key '${sgKey}' (from a hat) not found in org.orgSecurityGroups for position ${positionsCopy[i].partKey}`);
                        }
                    } else {
                        // Optionally log if an item from a hat's securityGroupsArray was not an object with a string key
                        // console.warn('Item from a hat\'s securityGroupsArray was not a valid SG object reference:', sgObjectFromHat, `for position ${positionsCopy[i].partKey}`);
                    }
                }
            }
            positionsCopy[i].amalgamatedSecurityGroupsFromHats = resolvedAmalgamatedSGsFromHats;

            // positionsCopy[i].securityGroupArray = resolvedAmalgamatedSGsFromHats;
        }

        return positionsCopy;
    }

    buildSecurityGroupsRelationshipsObject(
        positionsCopy: any,
        partToStartFromPathSplitString: string,
        org: any,
        tree: string
    ) {
        for (let i = 0; i < positionsCopy.length; i++) {
            const path = positionsCopy[i].path;
            const name = positionsCopy[i].name;

            let pathSplitString = '';

            pathSplitString = path.substring(partToStartFromPathSplitString.length, path.length);

            const partData = {
                background: '#007bff',
                color: 'white',
                selected: true,
            };

            const inheritedSecurityGroupsForPosition =
                positionsCopy[i].inheritanceObjects.secondaryPositionSecurityGroups;
            positionsCopy[i].inheritedSecurityGroupsForPosition = inheritedSecurityGroupsForPosition;

            // // console.log("positionsCopy[i] : ", positionsCopy[i]);
            const positionUsers = positionsCopy[i].userDetailsArray;
            const allSecurityGroupsForPosition = [
                ...positionsCopy[i].inheritanceObjects.primaryPositionSecurityGroups,
                ...positionsCopy[i].inheritanceObjects.secondaryPositionSecurityGroups,
            ];
            // // console.log("allSecurityGroupsForPosition : ", allSecurityGroupsForPosition);

            const positionUsersObjectArray2: any = [];

            if (positionUsers !== undefined && positionUsers.length > 0) {
                for (const user of positionUsers) {
                    const userFirebaseObject = this.orgUsers.find((o: any) => o.uid === user.key);
                    // // console.log("userFirebaseObject : ", `userFirebaseObject`);

                    // get the keys of the user.positionsObjects
                    const userPositionObjectKeys = Object.keys(user.positionsObjects);
                    // find where the key of the user on user.positionsObjects is same as position on positionsCopy
                    const userPositionKey: any = userPositionObjectKeys.find((o: any) => o === positionsCopy[i].key);
                    // // console.log("userPositionKey : ", userPositionKey);
                    const userPositionObject = user.positionsObjects[userPositionKey];
                    // // console.log("userPositionObject : ", userPositionObject);

                    const orgSecurityGroupsSubColFeatureObject = [];

                    for (const featureKey in userPositionObject.featureDetails) {
                        const feature = userPositionObject.featureDetails[featureKey];
                        const isInherited = feature.inherited ? true : false;

                        const featurePermissions = feature.permissions ? feature.permissions : undefined;
                        const inheritedFeaturePermissions = feature.inherited
                            ? feature.inherited.permissions
                            : undefined;
                        // // console.log("inheritedFeaturePermissions : ", featurePermissions);
                        // // console.log("inheritedFeatureDetail **** : ", inheritedFeaturePermissions);

                        // // console.log("feature ####### :  ", feature);

                        // featureDetail.permissions = [...new Set([...accPermissions, ...featureDetail.permissions])];

                        const completeFeatureInherited =
                            featurePermissions !== undefined &&
                            inheritedFeaturePermissions !== undefined &&
                            JSON.stringify(featurePermissions.sort()) ===
                                JSON.stringify(inheritedFeaturePermissions.sort())
                                ? true
                                : false;

                        const permissionsObjectArray: any = [];

                        const securityGroupWithEmptyPermissionsObject: any = {};
                        const securityGroupWithEmptyPermissionsObjectArray: any = [];
                        if (feature.permissions.length > 0) {
                            for (const permission of feature.permissions) {
                                const inheritedPermission = feature.inherited
                                    ? feature.inherited.permissions.find((o: any) => o === permission)
                                    : undefined;
                                const permissionsObject: any = {};
                                if (isInherited && inheritedPermission) {
                                    const inheritedDetailsForPermission =
                                        feature.inherited.inheritanceDetails[featureKey][permission];
                                    // // console.log("inheritedDetailsForPermission : ", inheritedDetailsForPermission);

                                    // find the sgs for the inherited permission using the sg key and looking up the sg in allSecurityGroupsForPosition
                                    const securityGroupsForPermission = inheritedDetailsForPermission.map((o: any) =>
                                        allSecurityGroupsForPosition.find((sg: any) => sg.key === o)
                                    );
                                    // // console.log("securityGroupsForPermission : ", permission, " ", securityGroupsForPermission);
                                    const securityGroupsForPermissionObjectArray: any = [];
                                    for (const sg of securityGroupsForPermission) {
                                        if (sg.featureDetails[featureKey].permissions.includes(permission)) {
                                            const securityGroupObject: any = {
                                                selected: true,
                                                color: 'white',
                                                background: 'green',
                                                data: {
                                                    depth: 2,
                                                    key: sg.key,
                                                    name: sg.name,
                                                    securityGroupName: sg.name,
                                                    inherited: true,
                                                    hasPermissions: true,
                                                    type: 'securityGroup',
                                                    userName: '',
                                                    uid: '',
                                                },
                                                children: [],
                                            };
                                            securityGroupsForPermissionObjectArray.push(securityGroupObject);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    public getSecurityGroupsWithSamePosition(org: any, sgSearch: any): any[] {
        const securityGroupsWithSamePositionArray: any = [];
        for (const sg of org.securityGroups) {
            if (sg.orgPosition.key === sgSearch.orgPosition.key && sg.key !== sgSearch.key) {
                securityGroupsWithSamePositionArray.push(sgSearch);
            }
        }
        return securityGroupsWithSamePositionArray;
    }

    /**
     * Appends all child positions to the provided organizational parts and removes duplicates.
     *
     * This function processes a hierarchical structure of organizational parts, starting from the
     * first part in the provided array. It iteratively traverses through all child parts, collecting
     * them into a list while ensuring no duplicates are included in the final result.
     *
     * This builds the rudimentary tree structure of the organization, which can be used for various purposes.
     *
     * @param orgParts - An array of organizational parts, where each part contains properties such as:
     *   - `depth`: The hierarchical depth of the part.
     *   - `parentPartKey`: The key of the parent part.
     *   - `partKey`: The unique key of the part.
     *   - `partType`: The type of the part (e.g., 'unit').
     *
     * @returns An array of organizational parts, including the initial part and all its child parts,
     *          with duplicates removed.
     *
     * @remarks
     * - The function assumes that the input `orgParts` array is sorted or structured in a way that
     *   allows hierarchical traversal based on `depth` and `parentPartKey`.
     * - The `partType` property is currently not used to filter parts, but it is present in the logic
     *   for potential future use.
     * - The function logs intermediate steps to the console for debugging purposes.
     */
    public appendAllChildPositionsToOrgParts(orgParts: any) {
        // console.log("appendAllChildPositionsToOrgParts > orgParts : ", orgParts);
        let process = true;
        // const firstPart = sg.orgPosition;
        const firstPart = orgParts[0];
        // console.log("appendAllChildPositionsToOrgParts > firstPart : ", JSON.stringify(firstPart));
        // let children = orgParts.filter((o: any) => o.depth === firstPart?.depth + 1 && o.parentPartKey === firstPart?.partKey && o.partType !== 'section' && o.partType !== 'unit');
        let children = orgParts.filter(
            (o: any) => o.depth === firstPart?.depth + 1 && o.parentPartKey === firstPart?.partKey
        );

        // console.log("appendAllChildPositionsToOrgParts > children : ", children);
        const positionsWillInheritFromSecurityGroupAbove2: any = [];
        positionsWillInheritFromSecurityGroupAbove2.push(firstPart);
        positionsWillInheritFromSecurityGroupAbove2.push(...children);

        let childrenOfCurrentIteration: any = [];
        let childrenOfCurrentPart: any = [];
        while (process) {
            childrenOfCurrentIteration = [];
            for (const child of children) {
                // childrenOfCurrentPart = orgParts.filter((o: any) => o.depth === child?.depth + 1 && o.parentPartKey === child?.partKey && o.partType !== 'section' && o.partType !== 'unit' );
                childrenOfCurrentPart = orgParts.filter(
                    (o: any) => o.depth === child?.depth + 1 && o.parentPartKey === child?.partKey
                );
                childrenOfCurrentIteration.push(...childrenOfCurrentPart);
                positionsWillInheritFromSecurityGroupAbove2.push(child);
            }

            if (childrenOfCurrentIteration.length > 0) {
                // more children to process
                children = childrenOfCurrentIteration;
                // console.log("appendAllChildPositionsToOrgParts > children 2 : ", children);
            } else {
                process = false;
            }
        }

        // remove duplicates on positionsWillInheritFromSecurityGroupAbove2
        const positionsWillInheritFromSecurityGroupAboveNoDuplicates2: any = [];
        for (const position of positionsWillInheritFromSecurityGroupAbove2) {
            if (
                !positionsWillInheritFromSecurityGroupAboveNoDuplicates2.find(
                    (o: any) => o.partKey === position.partKey
                )
            ) {
                positionsWillInheritFromSecurityGroupAboveNoDuplicates2.push(position);
            }
        }

        return positionsWillInheritFromSecurityGroupAboveNoDuplicates2;
    }

    public positionsWillInheritFromSecurityGroupBelow(org: any, sg: any) {
        let process = true;
        const orgParts = org.orgParts;
        let securityGroupPosition = sg.orgPosition;
        const positionsWillInheritFromSecurityGroupBelow: any = [];
        while (process) {
            const position = orgParts.find((o: any) => o.partKey === securityGroupPosition?.parentPartKey);
            if (position) {
                positionsWillInheritFromSecurityGroupBelow.push(position);
                securityGroupPosition = position;
            } else {
                process = false;
            }
        }

        return positionsWillInheritFromSecurityGroupBelow;
    }

    public extractSecurityGroupsForInheritedPositions(positionsCopy: any) {
        const inheritanceObject: any = {};
        const inheritanceObjects: any = {};
        // const childrenSecurityGroups: any = [];
        for (const part of positionsCopy) {
            // place all the securityGroups of the primary part into object
            inheritanceObject.primaryPositionSecurityGroups = part.securityGroupArray;

            // work through every child of part down
            const { positionsWillInheritFromSecurityGroupAbove, childrenSecurityGroups } =
                this.processAllChildrenAndGetSecurityGroups(part, positionsCopy);

            // place all the sgs of the secondary parts into object
            inheritanceObject.secondaryPositionSecurityGroups = childrenSecurityGroups;

            part.inheritanceObjects = deepCopy(inheritanceObject);
            inheritanceObjects[part.name] = deepCopy(inheritanceObject);
        }
    }

    processAllChildrenAndGetSecurityGroups(orgPosition: any, orgParts: any) {
        let process = true;
        const firstPart = orgPosition;
        let children = orgParts.filter(
            (o: any) => o.depth === firstPart?.depth + 1 && o.parentPartKey === firstPart?.partKey
        );

        const positionsWillInheritFromSecurityGroupAbove = [];
        positionsWillInheritFromSecurityGroupAbove.push(firstPart);
        positionsWillInheritFromSecurityGroupAbove.push(...children);

        let children2 = [];
        let childsChildren = [];
        const childrenSecurityGroups = [];
        while (process) {
            // // console.log("children 2 : ", children);
            children2 = [];
            for (const child of children) {
                childsChildren = orgParts.filter(
                    (o: any) => o.depth === child?.depth + 1 && o.parentPartKey === child?.partKey
                );
                children2.push(...childsChildren);

                // process the child and get all the sgs of its children
                childrenSecurityGroups.push(...child.securityGroupArray);
                positionsWillInheritFromSecurityGroupAbove.push(child);
            }

            if (children2.length > 0) {
                // more children to process
                children = children2;
            } else {
                process = false;
            }
        }
        return { positionsWillInheritFromSecurityGroupAbove, childrenSecurityGroups };
    }

    extractUserDetailsForPositions(positions: any) {
        const userObjectArray: UserDetails[] = []; // build an array of users for this position

        // process for every position - getting the primary securityGroups and inherited securityGroups and group together by user
        for (let i = 0; i < positions.length; i++) {
            const positionCopy = deepCopy(positions[i]);

            const primaryPositionSecurityGroupsByUser = [];
            const primaryPositionSecurityGroups = positionCopy.inheritanceObjects.primaryPositionSecurityGroups;
            // work through the sgs of the position and group by user
            for (const sg of primaryPositionSecurityGroups) {
                // group the sgs by user
                let allPrimarySecurityGroupsForUser = [];
                if (sg.assignedTo !== '') {
                    // add sg to array
                    allPrimarySecurityGroupsForUser = primaryPositionSecurityGroups.filter(
                        (o: any) => o.assignedTo === sg.assignedTo
                    );
                }
                if (
                    primaryPositionSecurityGroupsByUser.find(
                        (o: any) => o.assignedTo === sg.assignedTo && sg.assignedTo !== ''
                    ) === undefined
                ) {
                    primaryPositionSecurityGroupsByUser.push({ [sg.assignedTo]: allPrimarySecurityGroupsForUser });
                }
            }

            const secondaryPositionsSecurityGroups = positionCopy.inheritanceObjects.secondaryPositionSecurityGroups;
            for (const user of primaryPositionSecurityGroupsByUser) {
                const userKey: any = Object.keys(user)[0]; //?
                if (userKey === '') continue;
                const securityGroups =
                    primaryPositionSecurityGroupsByUser.find((o: any) =>
                        Object.keys(o).find((oo: any) => oo === userKey)
                    )?.[userKey] || [];
                const featureDetails = this.buildFeatureDetails(
                    securityGroups,
                    secondaryPositionsSecurityGroups,
                    positionCopy
                );
                const orgUser = this.org.orgUsers ? this.org.orgUsers[userKey] : undefined;

                userObjectArray.push({
                    key: userKey,
                    name: orgUser?.username || orgUser?.name || '',
                    positions: [positionCopy],
                    primaryPositionSecurityGroupsForUser: [...securityGroups],
                    secondaryPositionsSecurityGroups: [...secondaryPositionsSecurityGroups],
                    positionsObjects: {
                        [positionCopy.key]: {
                            position: [positionCopy],
                            primaryPositionSecurityGroups: [...securityGroups],
                            secondaryPositionsSecurityGroups: [...secondaryPositionsSecurityGroups],
                            featureDetails: featureDetails,
                        },
                    },
                });
            }
        }

        // New logic to process users from org.orgHats
        const userDetailsMapFromHats = new Map<string, UserDetails>(); // Key: userKey

        if (this.org && this.org.orgHats && this.org.orgHats.hatsKeys && Array.isArray(this.org.orgHats.hatsKeys)) {
            for (const position of positions) {
                // Iterate through all positions in the org structure
                const selectedPositionPartKey = 'OCucO9hNBNVY3sIBVtMt';
                const currentPositionPartKey = position.partKey;
                const usersProcessedForThisPositionFromHats = new Set<string>(); // Tracks users added for THIS position from hats

                for (const hatKey of this.org.orgHats.hatsKeys) {
                    // Iterate through all hats
                    const currentHat = this.org.orgHats[hatKey];
                    if (currentPositionPartKey === selectedPositionPartKey) {
                        console.log('🚀 ~ extractUserDetailsForPositions ~ currentHat:', currentHat);
                    }
                    if (
                        currentHat &&
                        currentHat.positionArray &&
                        Array.isArray(currentHat.positionArray) &&
                        ((currentHat.assignedToArray && Array.isArray(currentHat.assignedToArray)) ||
                            (currentHat.pendingAssignedToArray && Array.isArray(currentHat.pendingAssignedToArray)))
                    ) {
                        // Check if THIS hat is associated with the CURRENT position
                        const hatMatchesPosition = currentHat.positionArray.some(
                            (partKey: any) => partKey && partKey === currentPositionPartKey
                        );
                        if (currentPositionPartKey === selectedPositionPartKey) {
                            console.log(
                                '🚀 ~ extractUserDetailsForPositions ~ hatMatchesPosition:',
                                hatMatchesPosition
                            );
                        }
                        if (hatMatchesPosition) {
                            // Resolve SGs for THIS hat
                            const hatSecurityGroupsResolved: SecurityGroupDetails[] = [];
                            if (
                                currentHat.securityGroupsArray &&
                                Array.isArray(currentHat.securityGroupsArray) &&
                                this.org.orgSecurityGroups
                            ) {
                                for (const sgFromHatKey of currentHat.securityGroupsArray) {
                                    if (sgFromHatKey) {
                                        const canonicalSgObject = this.org.orgSecurityGroups[sgFromHatKey];
                                        if (canonicalSgObject) {
                                            hatSecurityGroupsResolved.push(canonicalSgObject);
                                        }
                                    }
                                }
                            }

                            for (const userKey of currentHat.assignedToArray) {
                                // Iterate through users on THIS hat
                                if (!userKey || typeof userKey !== 'string') continue;

                                // If this user has already been processed for THIS SPECIFIC POSITION (from any hat), skip.
                                if (usersProcessedForThisPositionFromHats.has(userKey)) {
                                    continue;
                                }

                                const orgUser =
                                    this.org.orgUsers && this.org.orgUsers[userKey]
                                        ? this.org.orgUsers[userKey]
                                        : { name: 'Unknown User', username: 'Unknown User' };

                                // DEBUG: Log user lookup details
                                // console.log('[OrgTree] User lookup:', {
                                //     userKey,
                                //     found: !!this.org.orgUsers?.[userKey],
                                //     orgUserName: orgUser?.name,
                                //     orgUserUsername: orgUser?.username,
                                //     orgUserKeys: orgUser ? Object.keys(orgUser) : []
                                // });

                                // DEBUG: Log when user lookup fails
                                if (!this.org.orgUsers || !this.org.orgUsers[userKey]) {
                                    console.warn('[OrgTree] User lookup FAILED:', {
                                        userKey,
                                        hasOrgUsers: !!this.org.orgUsers,
                                        orgUsersKeys: this.org.orgUsers
                                            ? Object.keys(this.org.orgUsers).filter((k) => k !== 'userKeys')
                                            : [],
                                        userKeysArray: this.org.orgUsers?.userKeys || [],
                                    });
                                }

                                const secondarySGsForHatUser: any[] = []; // Secondary SGs are empty for direct hat assignments
                                const featureDetails = this.buildFeatureDetails(
                                    hatSecurityGroupsResolved,
                                    secondarySGsForHatUser,
                                    position
                                );

                                let userDetailEntry = userDetailsMapFromHats.get(userKey);

                                if (!userDetailEntry) {
                                    // First time seeing this user globally (from hats processing)
                                    userDetailEntry = {
                                        key: userKey,
                                        name: orgUser?.username || orgUser?.name || 'User not found',
                                        positions: [], // Will be populated below
                                        primaryPositionSecurityGroupsForUser: [], // Initialize as empty, will be aggregated
                                        secondaryPositionsSecurityGroups: [], // Remains empty at top level for hats logic
                                        positionsObjects: {},
                                        awaitingAcceptance: orgUser?.status === 'pending',
                                    };
                                }

                                // Add/update position-specific info for this user
                                // Ensure the position itself is in the userDetailEntry.positions array (unique by partKey)
                                if (!userDetailEntry.positions.some((p) => p.partKey === currentPositionPartKey)) {
                                    userDetailEntry.positions.push(deepCopy(position));
                                }

                                // Add/update the positionsObjects entry for this specific position.
                                userDetailEntry.positionsObjects[currentPositionPartKey] = {
                                    position: [deepCopy(position)], // Storing as an array for consistency
                                    primaryPositionSecurityGroups: deepCopy(hatSecurityGroupsResolved),
                                    secondaryPositionsSecurityGroups: deepCopy(secondarySGsForHatUser),
                                    featureDetails: deepCopy(featureDetails),
                                };
                                if (currentPositionPartKey === selectedPositionPartKey) {
                                    console.log(
                                        '🚀 ~ extractUserDetailsForPositions ~ userDetailEntry:',
                                        userDetailEntry
                                    );
                                }
                                // Rebuild the aggregated primaryPositionSecurityGroupsForUser for this userDetailEntry
                                const aggregatedSGs = new Map<string, SecurityGroupDetails>();
                                for (const posKey in userDetailEntry.positionsObjects) {
                                    const posObj = userDetailEntry.positionsObjects[posKey];
                                    if (posObj && posObj.primaryPositionSecurityGroups) {
                                        for (const sg of posObj.primaryPositionSecurityGroups) {
                                            if (sg && sg.key) {
                                                // Ensure sg and sg.key are valid
                                                aggregatedSGs.set(sg.key, sg); // Use map for uniqueness by key
                                            }
                                        }
                                    }
                                }
                                userDetailEntry.primaryPositionSecurityGroupsForUser = Array.from(
                                    aggregatedSGs.values()
                                );

                                userDetailsMapFromHats.set(userKey, userDetailEntry); // Set/update the entry in the map
                                usersProcessedForThisPositionFromHats.add(userKey); // Mark user as processed for THIS position from hats
                            }

                            // Process PENDING users from pendingAssignedToArray
                            const pendingUsers = currentHat.pendingAssignedToArray || [];
                            for (const userKey of pendingUsers) {
                                if (!userKey || typeof userKey !== 'string') continue;
                                // Skip if already processed (active or pending) for this position
                                if (usersProcessedForThisPositionFromHats.has(userKey)) continue;

                                const orgUser =
                                    this.org.orgUsers && this.org.orgUsers[userKey]
                                        ? this.org.orgUsers[userKey]
                                        : { name: 'Unknown User', username: 'Unknown User' };

                                const secondarySGsForHatUser: any[] = [];
                                const featureDetails = this.buildFeatureDetails(
                                    hatSecurityGroupsResolved,
                                    secondarySGsForHatUser,
                                    position
                                );

                                let userDetailEntry = userDetailsMapFromHats.get(userKey);

                                if (!userDetailEntry) {
                                    userDetailEntry = {
                                        key: userKey,
                                        name: orgUser?.username || orgUser?.name || 'User not found',
                                        positions: [],
                                        primaryPositionSecurityGroupsForUser: [],
                                        secondaryPositionsSecurityGroups: [],
                                        positionsObjects: {},
                                        pendingPositionPartKeys: new Set<string>(),
                                        awaitingAcceptance: orgUser?.status === 'pending',
                                    };
                                }

                                // Track this position as pending for this user
                                if (!userDetailEntry.pendingPositionPartKeys) {
                                    userDetailEntry.pendingPositionPartKeys = new Set<string>();
                                }
                                userDetailEntry.pendingPositionPartKeys.add(currentPositionPartKey);

                                if (!userDetailEntry.positions.some((p) => p.partKey === currentPositionPartKey)) {
                                    userDetailEntry.positions.push(deepCopy(position));
                                }

                                userDetailEntry.positionsObjects[currentPositionPartKey] = {
                                    position: [deepCopy(position)],
                                    primaryPositionSecurityGroups: deepCopy(hatSecurityGroupsResolved),
                                    secondaryPositionsSecurityGroups: deepCopy(secondarySGsForHatUser),
                                    featureDetails: deepCopy(featureDetails),
                                };

                                const aggregatedSGs = new Map<string, SecurityGroupDetails>();
                                for (const posKey in userDetailEntry.positionsObjects) {
                                    const posObj = userDetailEntry.positionsObjects[posKey];
                                    if (posObj && posObj.primaryPositionSecurityGroups) {
                                        for (const sg of posObj.primaryPositionSecurityGroups) {
                                            if (sg && sg.key) {
                                                aggregatedSGs.set(sg.key, sg);
                                            }
                                        }
                                    }
                                }
                                userDetailEntry.primaryPositionSecurityGroupsForUser = Array.from(
                                    aggregatedSGs.values()
                                );

                                userDetailsMapFromHats.set(userKey, userDetailEntry);
                                usersProcessedForThisPositionFromHats.add(userKey);
                            }
                        }
                    }
                }
            }
        }
        const userObjectArrayFromHats = Array.from(userDetailsMapFromHats.values());

        // return userObjectArray;
        return userObjectArrayFromHats;
    }

    buildFeatureDetails(primeSecurityGroups: any, inheritedSecurityGroups: any, forPosition: any) {
        // console.log("buildFeatureDetails > primeSecurityGroups : ", primeSecurityGroups);
        // console.log("buildFeatureDetails > inheritedSecurityGroups : ", inheritedSecurityGroups);
        // console.log('----')
        // merge all featureDetails of primeSecurityGroups into one sg object
        const mergedPrimeSecurityGroupsFeatureDetails = primeSecurityGroups.reduce(
            (mergedSecurityGroup: any, sg: any) => {
                // console.log("mergedPrimeSecurityGroupsFeatureDetails > mergedSecurityGroup : ", mergedSecurityGroup);
                // console.log("mergedPrimeSecurityGroupsFeatureDetails > sg : ", sg);

                const securityGroupFeatureDetails = sg.featureDetails;
                const securityGroupFeatureDetailsKeys = Object.keys(securityGroupFeatureDetails);
                if (mergedSecurityGroup.securityGroups === undefined) {
                    mergedSecurityGroup.securityGroups = [];
                }
                mergedSecurityGroup.securityGroups = [...mergedSecurityGroup.securityGroups, sg];
                securityGroupFeatureDetailsKeys.forEach((key) => {
                    const featureDetail = securityGroupFeatureDetails[key];
                    const accPermissions = mergedSecurityGroup.featureDetails[key]?.permissions || [];
                    featureDetail.permissions = [...new Set([...accPermissions, ...featureDetail.permissions])];
                    mergedSecurityGroup.featureDetails[key] = featureDetail;
                });
                return mergedSecurityGroup;
            },
            { featureDetails: {}, securityGroups: [] }
        );

        // merge all featureDetails of inheritedSecurityGroups into one sg object
        const mergedInheritedSecurityGroupsFeatureDetails = inheritedSecurityGroups.reduce(
            (mergedSecurityGroup: any, sg: any) => {
                const securityGroupFeatureDetails = sg.featureDetails;
                const securityGroupFeatureDetailsKeys = Object.keys(securityGroupFeatureDetails);
                if (mergedSecurityGroup.securityGroups === undefined) {
                    mergedSecurityGroup.securityGroups = [];
                }
                mergedSecurityGroup.securityGroups = [...mergedSecurityGroup.securityGroups, sg];
                securityGroupFeatureDetailsKeys.forEach((key) => {
                    const featureDetail = securityGroupFeatureDetails[key];
                    const accPermissions = mergedSecurityGroup.featureDetails[key]?.permissions || [];
                    featureDetail.permissions = [...new Set([...accPermissions, ...featureDetail.permissions])];
                    mergedSecurityGroup.featureDetails[key] = featureDetail;
                });
                return mergedSecurityGroup;
            },
            { featureDetails: {}, securityGroups: [] }
        );

        // find all matching features where the permissions are different
        const mergedFeatureDetailsKeys = Object.keys(mergedPrimeSecurityGroupsFeatureDetails.featureDetails);
        const mergedFeatureDetails = mergedFeatureDetailsKeys.reduce((mergedFeatureDetails: any, key: any) => {
            let message = '';
            const primeFeatureDetail = mergedPrimeSecurityGroupsFeatureDetails.featureDetails[key];
            const inheritedFeatureDetail = mergedInheritedSecurityGroupsFeatureDetails.featureDetails[key] || {
                permissions: [],
            };

            // // console.log("primeFeatureDetail : ", primeFeatureDetail);
            // // console.log("inheritedFeatureDetail : ", inheritedFeatureDetail);
            if (
                JSON.stringify(primeFeatureDetail.permissions.sort()) !==
                JSON.stringify(inheritedFeatureDetail.permissions.sort())
            ) {
                const mergedFeatureDetailsOfPrimeAndInherited = {
                    // ...primeFeatureDetail,
                    // ...inheritedFeatureDetail,
                    permissions: [
                        ...new Set([...primeFeatureDetail.permissions, ...inheritedFeatureDetail.permissions]),
                    ],
                    name: primeFeatureDetail.name,
                    key: primeFeatureDetail.key,
                };
                // find the difference between the two permissions
                const differencePermissions1 = primeFeatureDetail.permissions.filter(
                    (permission: any) => !inheritedFeatureDetail.permissions.includes(permission)
                );
                // // console.log("differencePermissions1: ", differencePermissions1);
                const differencePermissions2 = inheritedFeatureDetail.permissions.filter(
                    (permission: any) => !primeFeatureDetail.permissions.includes(permission)
                );
                // // console.log("differencePermissions2: ", differencePermissions2);
                const differencePermissions = [...differencePermissions1, ...differencePermissions2];
                // // console.log("differencePermissions: ", differencePermissions);
                if (differencePermissions2.length > 0) {
                    message = `The following permissions are not on the prime securityGroup: ${differencePermissions2.join(', ')}`;
                }

                const securityGroupsWithFeatureForPermission: any = {};
                for (const permission of differencePermissions2) {
                    for (const sg of inheritedSecurityGroups) {
                        for (const key in sg.featureDetails) {
                            const feature = sg.featureDetails[key];
                            const permissions = feature.permissions;
                            if (feature.name === key && permissions.includes(permission)) {
                                if (securityGroupsWithFeatureForPermission[key] === undefined) {
                                    securityGroupsWithFeatureForPermission[key] = { [permission]: [sg.key] };
                                } else {
                                    if (securityGroupsWithFeatureForPermission[key][permission] === undefined) {
                                        securityGroupsWithFeatureForPermission[key][permission] = [sg.key];
                                    } else {
                                        securityGroupsWithFeatureForPermission[key][permission].push(sg.key);
                                    }
                                }
                            }
                        }
                    }
                }

                // merge mergedPrimeSecurityGroupsFeatureDetails and
                mergedFeatureDetails[key] = {
                    primeFeatureDetail,
                    inheritedFeatureDetail,
                    mergedPermissions: mergedFeatureDetailsOfPrimeAndInherited,
                    differencePermissions: differencePermissions2,
                    message,
                    inheritanceDetails: securityGroupsWithFeatureForPermission,
                };
            } else {
                mergedFeatureDetails[key] = {
                    primeFeatureDetail,
                    inheritedFeatureDetail,
                    mergedPermissions: primeFeatureDetail,
                    differencePermissions: [],
                    message,
                    inheritanceDetails: {},
                };
            }
            return mergedFeatureDetails;
        }, {});

        // find where the feature is not on the prime but on inherited
        const mergedFeatureDetailsKeys2 = Object.keys(mergedInheritedSecurityGroupsFeatureDetails.featureDetails);
        const mergedFeatureDetails2: any = mergedFeatureDetailsKeys2.reduce((mergedFeatureDetails2: any, key: any) => {
            let message = '';
            const primeFeatureDetail = mergedPrimeSecurityGroupsFeatureDetails.featureDetails[key];
            const inheritedFeatureDetail = mergedInheritedSecurityGroupsFeatureDetails.featureDetails[key];
            if (!primeFeatureDetail) {
                const mergedFeatureDetailsOfPrimeAndInherited = {
                    permissions: [...new Set([...inheritedFeatureDetail.permissions])],
                    name: inheritedFeatureDetail.name,
                    key: inheritedFeatureDetail.key,
                };

                // // console.log("inheritedFeatureDetail : ", inheritedFeatureDetail);
                const securityGroupsWithFeatureNoPermissions: any = {};
                // for(const feature of inheritedFeatureDetail) {
                for (const sg of inheritedSecurityGroups) {
                    for (const key in sg.featureDetails) {
                        const feature = sg.featureDetails[key];
                        const permissions = feature.permissions;
                        if (inheritedFeatureDetail.permissions.length > 0) {
                            // // console.log("inheritedFeatureDetail.permissions : ", inheritedFeatureDetail.permissions);

                            for (const permission of inheritedFeatureDetail.permissions) {
                                if (feature.name === key) {
                                    if (securityGroupsWithFeatureNoPermissions[key] === undefined) {
                                        securityGroupsWithFeatureNoPermissions[key] = { [permission]: [sg.key] };
                                    } else {
                                        if (securityGroupsWithFeatureNoPermissions[key][permission] === undefined) {
                                            securityGroupsWithFeatureNoPermissions[key][permission] = [sg.key];
                                        } else {
                                            securityGroupsWithFeatureNoPermissions[key][permission].push(sg.key);
                                        }
                                    }
                                }
                            }
                        } else {
                            // // console.log("inheritedFeatureDetail.permissions : ", inheritedFeatureDetail.permissions);
                            if (feature.name === key) {
                                // // console.log("feature : ", feature.name, " sg name: ", sg.name, " sg key: ", sg.key, " permissions: ", permissions);
                                if (securityGroupsWithFeatureNoPermissions[key] === undefined) {
                                    securityGroupsWithFeatureNoPermissions[key] = [sg.key];
                                } else {
                                    if (securityGroupsWithFeatureNoPermissions[key] === undefined) {
                                        securityGroupsWithFeatureNoPermissions[key] = [sg.key];
                                    } else {
                                        securityGroupsWithFeatureNoPermissions[key].push(sg.key);
                                    }
                                }
                            }
                        }
                    }
                }

                // // console.log("mergedFeatureDetailsOfPrimeAndInherited ", mergedFeatureDetailsOfPrimeAndInherited);
                message = `The following features are not on the prime sg: ${key}`;
                mergedFeatureDetails2[key] = {
                    primeFeatureDetail: {},
                    inheritedFeatureDetail,
                    mergedPermissions: mergedFeatureDetailsOfPrimeAndInherited,
                    differencePermissions: [],
                    message,
                    inheritanceDetails: securityGroupsWithFeatureNoPermissions,
                };
            }
            return mergedFeatureDetails2;
        }, {});

        // // console.log("mergedFeatureDetails: ", mergedFeatureDetails);
        // // console.log("mergedFeatureDetails2: ", mergedFeatureDetails2);

        const featureDetails: any = {};
        for (const key in mergedFeatureDetails) {
            // // console.log("key: ", key);
            const featureDetail = mergedFeatureDetails[key];
            featureDetails[key] = featureDetail.mergedPermissions;
            if (featureDetail.message) {
                // // console.log("featureDetail.message: ", featureDetail.message);
                featureDetails[key].inherited = { permissions: [], inheritanceDetails: {} };
                featureDetails[key]['inherited'].permissions = featureDetail.differencePermissions;
                featureDetails[key]['inherited'].inheritanceDetails[key] = featureDetail.inheritanceDetails[key];
            }
        }

        for (const key in mergedFeatureDetails2) {
            // // console.log("key: ", key);
            const featureDetail = mergedFeatureDetails2[key];
            featureDetails[key] = featureDetail.mergedPermissions;
            if (featureDetail.message) {
                // // console.log("featureDetail.message: ", featureDetail.message);
                featureDetails[key].inherited = { permissions: [], inheritanceDetails: {} };
                featureDetails[key]['inherited'].permissions = featureDetail.mergedPermissions.permissions;
                featureDetails[key]['inherited'].inheritanceDetails[key] = featureDetail.inheritanceDetails[key];
            }
        }
        // // console.log("featureDetails: ", featureDetails);
        return featureDetails;
    }

    appendSectionsAndUnitsDetailsToParts(userObjectArrayCopy: any, positions: any, userDetails: any) {
        const sections: any = [];
        const units: any = [];
        const posts: any = [];
        const departments: any = [];

        for (const part of positions) {
            if (part.partType === 'section') {
                sections.push(part);
            } else if (part.partType === 'unit') {
                units.push(part);
            } else if (part.partType === 'post') {
                posts.push(part);
            } else if (part.partType === 'department') {
                departments.push(part);
            }
        }

        // Helper function to extract sibling index from path (e.g., "children.0.children.2.data" -> 2)
        const extractIndexFromPath = (path: string | undefined): number => {
            if (!path || typeof path !== 'string') return -1;
            const pathParts = path.split('.');
            for (let i = pathParts.length - 1; i >= 0; i--) {
                if (pathParts[i] === 'data' && i > 0 && /^\d+$/.test(pathParts[i - 1])) {
                    return parseInt(pathParts[i - 1], 10);
                }
            }
            return -1;
        };

        // Helper function to sort parts by their path index
        const sortByPathIndex = (parts: any[]): any[] => {
            return parts.sort((a, b) => {
                const aIndex = extractIndexFromPath(a.path);
                const bIndex = extractIndexFromPath(b.path);
                // If both have valid path indices, use them
                if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                }
                // Fall back to ID-based sorting if path is not available
                return (a.id || 0) - (b.id || 0);
            });
        };

        // Header-role weight for sorting header-strip posts (head < deputy < assistant)
        const HEADER_ROLE_WEIGHT: Record<string, number> = { head: 0, deputy: 1, assistant: 2 };
        const isHeaderPost = (p: any): boolean =>
            p?.partType === 'post' &&
            (p.headerRole === 'head' || p.headerRole === 'deputy' || p.headerRole === 'assistant');
        // A DETACHED post (`detachedGroup` set) is pulled OUT of both the header
        // strip and the body list and rendered in its own box elsewhere on the
        // board (e.g. Board of Directors beside the CEO). It still belongs to
        // this container in the data — only its placement changes.
        // Only the board root's posts may be detached (Board of Directors = the CEO's
        // peers, detached-group-rules.ts). A stray `detachedGroup` anywhere else is
        // ignored so the post renders in place instead of vanishing.
        const rootKey = boardRootPartKey(positions);
        const isDetachedPost = (p: any): boolean =>
            !!p?.detachedGroup && mayJoinDetachedGroup(p.partType, p.parentPartKey, rootKey);
        /**
         * Partition a posts array into [headerPosts, bodyPosts, detachedPosts].
         * Header posts (those with `headerRole`) are sorted head → deputy → assistant,
         * with path-index as a tiebreaker. Body posts retain their existing path-index order.
         * Detached posts (those with `detachedGroup`) are excluded from BOTH so they
         * never double-render — they are grouped and attached via `groupDetached`.
         */
        const partitionHeaderPosts = (posts: any[]): { headerPosts: any[]; bodyPosts: any[]; detachedPosts: any[] } => {
            const detachedPosts = posts.filter(isDetachedPost);
            const headerPosts = posts
                .filter((p) => isHeaderPost(p) && !isDetachedPost(p))
                .sort((a, b) => {
                    const wa = HEADER_ROLE_WEIGHT[a.headerRole] ?? 99;
                    const wb = HEADER_ROLE_WEIGHT[b.headerRole] ?? 99;
                    if (wa !== wb) return wa - wb;
                    return extractIndexFromPath(a.path) - extractIndexFromPath(b.path);
                });
            const bodyPosts = posts.filter((p) => !isHeaderPost(p) && !isDetachedPost(p));
            return { headerPosts, bodyPosts, detachedPosts };
        };
        /**
         * Group detached posts by their `detachedGroup` key into the shape the
         * renderer consumes: `[{ key, posts, connectsTo }]`. `connectsTo` is the
         * cosmetic connector target (first post in the group that declares one).
         * Returns [] when there are no detached posts (no empty bucket attached).
         */
        const groupDetached = (detachedPosts: any[]): any[] => {
            if (!detachedPosts || detachedPosts.length === 0) return [];
            const byKey: Record<string, any[]> = {};
            for (const p of detachedPosts) {
                if (!byKey[p.detachedGroup]) byKey[p.detachedGroup] = [];
                byKey[p.detachedGroup].push(p);
            }
            return Object.keys(byKey).map((key) => {
                const withTarget = byKey[key].find((p) => p.detachedGroupConnectsTo);
                return {
                    key,
                    posts: sortByPathIndex(byKey[key]),
                    connectsTo: (withTarget && withTarget.detachedGroupConnectsTo) || null,
                };
            });
        };

        // Posts in Units
        for (const unit of units) {
            // find the posts in the unit using partKey and parentPartKey
            const postsInUnit = posts.filter((o: any) => o.parentPartKey === unit.partKey);
            // Sort by path index to maintain correct order after reorder operations
            const sorted = sortByPathIndex(postsInUnit);
            const { headerPosts, bodyPosts, detachedPosts } = partitionHeaderPosts(sorted);
            unit.postsInUnit = bodyPosts;
            unit.headerPostsInUnit = headerPosts;
            const detachedGroups = groupDetached(detachedPosts);
            if (detachedGroups.length > 0) unit.detachedGroups = detachedGroups;
        }

        // Posts in Sections
        for (const section of sections) {
            // find the posts in the section using partKey and parentPartKey
            const postsInSection = posts.filter((o: any) => o.parentPartKey === section.partKey);
            // Sort by path index to maintain correct order after reorder operations
            const sorted = sortByPathIndex(postsInSection);
            const { headerPosts, bodyPosts, detachedPosts } = partitionHeaderPosts(sorted);
            section.postsInSection = bodyPosts;
            section.headerPostsInSection = headerPosts;
            const detachedGroups = groupDetached(detachedPosts);
            if (detachedGroups.length > 0) section.detachedGroups = detachedGroups;
        }

        // Units in Sections
        for (const section of sections) {
            // find the units in the section using partKey and parentPartKey
            const unitsInSection = units.filter((o: any) => o.parentPartKey === section.partKey);
            // Sort by path index to maintain correct order after reorder operations
            section.unitsInSection = sortByPathIndex(unitsInSection);
            // Debug logging commented out for production
            // if (unitsInSection.length > 1) {
            //     console.log(`🔄 Sorted ${unitsInSection.length} units in section "${section.name}":`,
            //         section.unitsInSection.map((u: any) => `${u.name} (path index: ${extractIndexFromPath(u.path)})`));
            // }
        }

        // Sections in Departments
        for (const department of departments) {
            // find the sections in the department using partKey and parentPartKey
            const sectionsInDepartment = sections.filter((o: any) => o.parentPartKey === department.partKey);
            // Sort by path index to maintain correct order after reorder operations
            department.sectionsInDepartment = sortByPathIndex(sectionsInDepartment);
            // Debug logging commented out for production
            // if (sectionsInDepartment.length > 1) {
            //     console.log(`🔄 Sorted ${sectionsInDepartment.length} sections in department "${department.name}":`,
            //         department.sectionsInDepartment.map((s: any) => `${s.name} (path index: ${extractIndexFromPath(s.path)})`));
            // }
        }

        // Append sectionsInDepartment to positions
        for (const position of positions) {
            // if position same as department in departments then push sectionsInDepartment to position in positions
            const department = departments.find((o: any) => o.partKey === position.partKey);
            if (department) {
                position.sectionsInDepartment = department.sectionsInDepartment;
            }
        }

        // Posts directly under Divisions, Executives, or Departments (rendered inside the parent card)
        for (const position of positions) {
            if (
                position.partType === 'division' ||
                position.partType === 'executive' ||
                position.partType === 'department'
            ) {
                const postsInParent = posts.filter((o: any) => o.parentPartKey === position.partKey);
                if (postsInParent.length > 0) {
                    const sorted = sortByPathIndex(postsInParent);
                    const { headerPosts, bodyPosts, detachedPosts } = partitionHeaderPosts(sorted);
                    position.postsInParent = bodyPosts;
                    position.headerPostsInParent = headerPosts;
                    const detachedGroups = groupDetached(detachedPosts);
                    if (detachedGroups.length > 0) position.detachedGroups = detachedGroups;
                }
            }
        }

        // ─────────────────────────────────────────────────────────────────────────
        // Interleaved body children — single sorted array used by the renderer
        // ─────────────────────────────────────────────────────────────────────────
        // The legacy split into separate `postsInParent` / `sectionsInDepartment`
        // (and `postsInSection` / `unitsInSection`) buckets caused the board to
        // always render all posts before all sections (and posts before units),
        // regardless of the actual sibling order persisted in the path index.
        //
        // To honour the order chosen in the editor (or via Reorder Children), we
        // produce a single `bodyChildren` array per container that interleaves
        // body posts with their sibling containers (sections under a department,
        // units under a section), all sorted by path index. The legacy buckets
        // are kept untouched for backwards compatibility (deprecated `no-user`,
        // `one-user`, `many-users` components still consume them).
        for (const position of positions) {
            if (position.partType === 'department') {
                const bodyPosts = position.postsInParent || [];
                const sectionsInDept = position.sectionsInDepartment || [];
                position.bodyChildren = sortByPathIndex([...bodyPosts, ...sectionsInDept]);
            } else if (position.partType === 'division' || position.partType === 'executive') {
                // No section children at this level — bodyChildren is just body posts.
                position.bodyChildren = position.postsInParent || [];
            }
        }
        for (const section of sections) {
            const bodyPosts = section.postsInSection || [];
            const unitsIn = section.unitsInSection || [];
            section.bodyChildren = sortByPathIndex([...bodyPosts, ...unitsIn]);
        }

        // console.log("1111111 🚀 ~ appendSectionsAndUnitsDetailsToParts ~ departments:", departments);
        return positions;
    }

    appendUserDetailsToParts(userObjectArrayCopy: any, positions: any, userDetails: any) {
        const userPartObjectArray: any = [];
        for (let i = 0; i < userObjectArrayCopy.length; i++) {
            const user = userObjectArrayCopy[i];
            // // console.log("user : ", user);
            const userKey = user.key;
            // // console.log("this.orgUsers : ", this.orgUsers);
            // const firebaseUserObject = this.orgUsers.users.find((o: any) => {
            //     if (o.uid === userKey) {
            //         return o;
            //     }
            // });

            // go through the parts on positions and add the user details to the parts
            let positionObject: any = [];
            for (let j = 0; j < user.positions.length; j++) {
                const position = user.positions[j];
                const positionKey = position.key;
                positionObject = deepCopy(positions).find((o: any) => {
                    if (o.key === positionKey) {
                        return o;
                    }
                });

                // Clone user and set isPending per-position (not globally)
                const userClone = { ...user };
                const posPartKey = positionObject.partKey || positionObject.key;
                userClone.isPending = !!(user.pendingPositionPartKeys && user.pendingPositionPartKeys.has(posPartKey));
                // Carry the global "awaiting invitation acceptance" flag per-position so the
                // tile template can hide the approve tick + render an INVITED badge for invitees
                // who have not yet accepted their org invitation.
                userClone.awaitingAcceptance = !!user.awaitingAcceptance;

                if (userPartObjectArray.find((o: any) => o.key === positionObject.key) === undefined) {
                    userPartObjectArray.push({
                        [positionObject.key]: {},
                        key: positionObject.key,
                        userDetailsArray: [userClone],
                    });
                } else {
                    // get index
                    const index = userPartObjectArray.findIndex((o: any) => o.key === positionObject.key);
                    // see if user already exist on userDetailsArray
                    if (
                        userPartObjectArray[index].userDetailsArray.find((o: any) => o.key === userClone.key) ===
                        undefined
                    ) {
                        userPartObjectArray[index].userDetailsArray.push(userClone);
                    }
                }
            }
        }

        let positionObject: any = [];
        // attach the user details to the parts
        for (let i = 0; i < userPartObjectArray.length; i++) {
            const userPartObject = userPartObjectArray[i];
            const userPartObjectKey = userPartObject.key;
            const userPartObjectUserDetailsArray = userPartObject.userDetailsArray;
            positionObject = positions.find((o: any) => {
                if (o.key === userPartObjectKey) {
                    return o;
                }
            });
            positionObject.userDetailsArray = userPartObjectUserDetailsArray;
        }

        // console.log("positionObject >>>>>> >>>>>>: ", positionObject);
        return positions;
    }

    buildUserSecurityGroupsRelationshipsObject(positionsCopy: any, partToStartFromPathSplitString: string) {
        for (let i = 0; i < positionsCopy.length; i++) {
            const path = positionsCopy[i].path;
            const name = positionsCopy[i].name;

            let pathSplitString = '';

            pathSplitString = path.substring(partToStartFromPathSplitString.length, path.length);

            const partData = {
                background: '#c2185b',
                color: 'white',
                selected: true,
            };

            // const inheritedSecurityGroupsForPosition = positionsCopy[i].inheritanceObjects.secondaryPositionSecurityGroups;
            // positionsCopy[i].inheritedSecurityGroupsForPosition = inheritedSecurityGroupsForPosition;

            // // console.log("positionsCopy[i] : ", positionsCopy[i]);
            const positionUsers = positionsCopy[i].userDetailsArray;
            const allSecurityGroupsForPosition = [
                ...positionsCopy[i].inheritanceObjects.primaryPositionSecurityGroups,
                ...positionsCopy[i].inheritanceObjects.secondaryPositionSecurityGroups,
            ];
            // // console.log("allSecurityGroupsForPosition : ", allSecurityGroupsForPosition);

            const positionUsersObjectArray2: any = [];

            if (positionUsers !== undefined && positionUsers.length > 0) {
                for (const user of positionUsers) {
                    const userFirebaseObject = this.orgUsers.find((o: any) => o.uid === user.key);
                    // // console.log("userFirebaseObject : ", `userFirebaseObject`);

                    // get the keys of the user.positionsObjects
                    const userPositionObjectKeys = Object.keys(user.positionsObjects);
                    // find where the key of the user on user.positionsObjects is same as position on positionsCopy
                    const userPositionKey: any = userPositionObjectKeys.find((o: any) => o === positionsCopy[i].key);
                    // // console.log("userPositionKey : ", userPositionKey);
                    const userPositionObject = user.positionsObjects[userPositionKey];
                    // // console.log("userPositionObject : ", userPositionObject);

                    const orgSecurityGroupsSubColFeatureObject = [];

                    for (const featureKey in userPositionObject.featureDetails) {
                        const feature = userPositionObject.featureDetails[featureKey];
                        const isInherited = feature.inherited ? true : false;

                        const featurePermissions = feature.permissions ? feature.permissions : undefined;
                        const inheritedFeaturePermissions = feature.inherited
                            ? feature.inherited.permissions
                            : undefined;
                        // // console.log("inheritedFeaturePermissions : ", featurePermissions);
                        // // console.log("inheritedFeatureDetail **** : ", inheritedFeaturePermissions);

                        // // console.log("feature ####### :  ", feature);

                        // featureDetail.permissions = [...new Set([...accPermissions, ...featureDetail.permissions])];

                        const completeFeatureInherited =
                            featurePermissions !== undefined &&
                            inheritedFeaturePermissions !== undefined &&
                            JSON.stringify(featurePermissions.sort()) ===
                                JSON.stringify(inheritedFeaturePermissions.sort())
                                ? true
                                : false;

                        const permissionsObjectArray: any = [];

                        let securityGroupWithEmptyPermissionsObject: any = {};
                        const securityGroupWithEmptyPermissionsObjectArray: any = [];
                        if (feature.permissions.length > 0) {
                            for (const permission of feature.permissions) {
                                const inheritedPermission = feature.inherited
                                    ? feature.inherited.permissions.find((o: any) => o === permission)
                                    : undefined;
                                let permissionsObject: any = {};
                                if (isInherited && inheritedPermission) {
                                    const inheritedDetailsForPermission =
                                        feature.inherited.inheritanceDetails[featureKey][permission];
                                    // // console.log("inheritedDetailsForPermission : ", inheritedDetailsForPermission);

                                    // find the sgs for the inherited permission using the sg key and looking up the sg in allSecurityGroupsForPosition
                                    const securityGroupsForPermission = inheritedDetailsForPermission.map((o: any) =>
                                        allSecurityGroupsForPosition.find((sg: any) => sg.key === o)
                                    );
                                    // // console.log("securityGroupsForPermission : ", permission, " ", securityGroupsForPermission);
                                    const securityGroupsForPermissionObjectArray: any = [];
                                    for (const sg of securityGroupsForPermission) {
                                        if (sg.featureDetails[featureKey].permissions.includes(permission)) {
                                            const securityGroupObject: any = {
                                                selected: true,
                                                color: 'white',
                                                background: 'green',
                                                data: {
                                                    depth: 2,
                                                    key: sg.key,
                                                    expandedKey:
                                                        'tree5-' +
                                                        this.org.key +
                                                        '-' +
                                                        '-' +
                                                        '-' +
                                                        featureKey +
                                                        '-' +
                                                        permission +
                                                        '-permission' +
                                                        '-usersOnPositionObject',
                                                    name: sg.name + '-' + sg.key,
                                                    multiUsers: sg.multiUsers,
                                                    type: 'securityGroup',
                                                    isChecked: false,
                                                },
                                                children: [],
                                            };

                                            securityGroupsForPermissionObjectArray.push(securityGroupObject);
                                        }
                                    }

                                    permissionsObject = {
                                        selected: true,
                                        color: 'white',
                                        background: '#bf60f4',
                                        data: {
                                            depth: 2,
                                            key: featureKey + '-' + permission,
                                            expandedKey:
                                                'tree5-' +
                                                this.org.key +
                                                '-' +
                                                '-' +
                                                '-' +
                                                featureKey +
                                                '-' +
                                                permission +
                                                '-permission' +
                                                '-usersOnPositionObject',
                                            name: permission,
                                            inherited: true,
                                            inheritedFromSecurityGroup: '',
                                            inheritedFromPosition: '',
                                            type: 'permission',
                                            isChecked: false,
                                        },
                                        children: securityGroupsForPermissionObjectArray,
                                    };
                                } else {
                                    permissionsObject = {
                                        selected: true,
                                        color: 'white',
                                        background: '#6060f4',
                                        data: {
                                            depth: 2,
                                            key: featureKey + '-' + permission,
                                            expandedKey:
                                                'tree5-' +
                                                this.org.key +
                                                '-' +
                                                '-' +
                                                '-' +
                                                featureKey +
                                                '-' +
                                                permission +
                                                '-permission' +
                                                '-usersOnPositionObject',
                                            name: permission,
                                            inherited: false,
                                            type: 'permission',
                                            isChecked: false,
                                        },
                                        children: [],
                                    };
                                }

                                permissionsObjectArray.push(permissionsObject);
                            }
                        } else {
                            // if (sg.featureDetails[featureKey].permissions.length === 0) {
                            // // console.log("sg.featureDetails[featureKey].permissions : ", sg.featureDetails[featureKey].permissions);

                            if (feature.inherited) {
                                const inheritedDetailsForFeature = feature.inherited.inheritanceDetails[featureKey];
                                // // console.log("inheritedDetailsForFeature : ", inheritedDetailsForFeature);

                                // find the sgs for the inherited feature using the sg key and looking up the sg in allSecurityGroupsForPosition
                                const securityGroupsForFeature = inheritedDetailsForFeature.map((o: any) =>
                                    allSecurityGroupsForPosition.find((sg: any) => sg.key === o)
                                );
                                for (const sg of securityGroupsForFeature) {
                                    securityGroupWithEmptyPermissionsObject = {
                                        selected: true,
                                        color: 'white',
                                        background: 'green',
                                        data: {
                                            depth: 2,
                                            key: sg.key,
                                            expandedKey:
                                                'tree5-' +
                                                this.org.key +
                                                '-' +
                                                '-' +
                                                '-' +
                                                featureKey +
                                                '-permission' +
                                                '-usersOnPositionObject',
                                            name: sg.name + '-' + sg.key,
                                            multiUsers: sg.multiUsers,
                                            type: 'securityGroup',
                                            isChecked: false,
                                        },
                                        children: [],
                                    };

                                    securityGroupWithEmptyPermissionsObjectArray.push(
                                        securityGroupWithEmptyPermissionsObject
                                    );

                                    // // console.log("securityGroupWithEmptyPermissionsObjectArray : ", securityGroupWithEmptyPermissionsObjectArray);
                                }

                                permissionsObjectArray.push(...securityGroupWithEmptyPermissionsObjectArray);
                            }
                        }

                        let featureObject: any = {};
                        if (completeFeatureInherited) {
                            featureObject = {
                                selected: true,
                                color: 'white',
                                background: '#bf60f4',
                                data: {
                                    depth: 2,
                                    key: featureKey,
                                    expandedKey:
                                        'tree5-' +
                                        this.org.key +
                                        '-' +
                                        '-' +
                                        '-' +
                                        featureKey +
                                        '-usersOnPositionObject',
                                    name: featureKey,
                                    inherited: true,
                                    hasPermissions: feature.permissions.length > 0 ? true : false,
                                    type: 'feature',
                                    isChecked: false,
                                },
                                children: permissionsObjectArray,
                            };
                        } else {
                            const inheritedMessage = isInherited ? '      **** INHERITANCE **** ' : '';
                            featureObject = {
                                selected: true,
                                color: 'white',
                                background: 'blue',
                                data: {
                                    depth: 2,
                                    key: featureKey,
                                    expandedKey:
                                        'tree5-' +
                                        this.org.key +
                                        '-' +
                                        '-' +
                                        '-' +
                                        featureKey +
                                        '-usersOnPositionObject',
                                    name: featureKey, // + inheritedMessage,
                                    inherited: isInherited ? true : false,
                                    hasPermissions: feature.permissions.length > 0 ? true : false,
                                    type: 'feature',
                                    isChecked: false,
                                },
                                children: permissionsObjectArray,
                            };
                        }
                        orgSecurityGroupsSubColFeatureObject.push(featureObject);
                    }

                    // if (orgSecurityGroupsSubColFeatureObject.length > 0) {
                    const allFeaturesForUserPositionObject: any = {
                        selected: true,
                        color: 'black',
                        background: '#ffa500',
                        data: {
                            depth: 2,
                            key: user.key + '-' + userPositionKey,
                            expandedKey: 'tree5-' + this.org.key + '-' + 'allFeaturesForUserPositionObject-' + user.key,
                            name:
                                'featureDetails For User ' +
                                userFirebaseObject.displayName +
                                ' on Position ' +
                                userPositionKey,
                            // multiUsers: sg.multiUsers,
                            type: 'allFeaturesForUserPositionObject',
                            isChecked: false,
                        },
                        children: orgSecurityGroupsSubColFeatureObject,
                    };

                    // if (orgSecurityGroupsSubColFeatureObject.length > 0) {
                    const usersObject: any = {
                        selected: true,
                        color: 'white',
                        background: '#c2185b',
                        data: {
                            depth: 2,
                            key: user.key,
                            expandedKey: 'tree5-' + this.org.key + '-' + 'usersOnPositionObject-' + user.key,
                            name: userFirebaseObject.displayName + ' - ' + user.key,
                            // multiUsers: sg.multiUsers,
                            type: 'usersOnPositionObject',
                            isChecked: false,
                        },
                        children: [allFeaturesForUserPositionObject],
                    };
                    positionUsersObjectArray2.push(usersObject);

                    // }
                }
            }

            if (positionUsersObjectArray2.length > 0) {
                const usersOnPositionObject: any = {
                    selected: true,
                    color: 'white',
                    background: '#c2185b',
                    data: {
                        depth: 1,
                        key: positionsCopy[i].name,
                        expandedKey: 'tree5-' + this.org.key + '-' + '-usersOnPositionMainObject',
                        name: 'Users on Position: ** ' + positionsCopy[i].name + ' **',
                        type: 'usersOnPositionMainObject',
                        isChecked: false,
                    },
                    children: positionUsersObjectArray2,
                };

                // // console.log("usersOnPositionObject : ", usersOnPositionObject);

                const flattenUsersOnPositionObject = flatten(usersOnPositionObject);

                positionsCopy[i].positionUsersFlat = flattenUsersOnPositionObject;

                positionsCopy[i].positionUsersObject = usersOnPositionObject;
            } else {
                positionsCopy[i].positionUsersFlat = {};

                positionsCopy[i].positionUsersObject = {};
            }
        }
    }

    // attach new tree to node of existing tree
    public attachTreeObjectToNodeOfTree(
        treeKey: any,
        nodePathToConnectTo: string,
        isFirstChild: boolean,
        treeObject: any
    ) {
        // find the tree with key in treesMap
        const tree = this.treesMap[treeKey].treeObject;
        const flattenedTree = this.flattenTree(tree);
        const flattenedTree2 = this.flattenTree(treeObject);
        const unflattenTree1 = unFlatten(flattenedTree);

        // const newTreeObject = newTree.returnTreeObjectAtPath(nodePathToConnectTo, treeKey);  //?
        const newTreeObject = treeObject.returnTreeObjectAtPath(nodePathToConnectTo, treeKey); //?
        const sizeOfChildren = newTreeObject.children.length;
        const indexOfChild = !isFirstChild ? sizeOfChildren : 0;

        // set the path of the key and value match in the flattenedTree
        const pathArray = nodePathToConnectTo.split('.');

        //pop last item off pathArray
        pathArray.pop();

        let pathArrayString = '';
        if (pathArray.length !== 0) {
            // console.log("pathArray: ", pathArray);
            for (let j = 0; j < pathArray.length; j++) {
                pathArrayString += pathArray[j] + '.';
            }
            pathArrayString = pathArrayString.substring(0, pathArrayString.length - 1) + '.children.' + indexOfChild;
        } else {
            pathArrayString = 'children.' + indexOfChild;
        }

        if (!isFirstChild) {
            for (const pathKey in flattenedTree2) {
                const path = pathArrayString + '.' + pathKey;
                const value = flattenedTree2[pathKey];
                // const newPath = pathArrayString + "." + path;
                flattenedTree[path] = value;
            }
        } else {
            const pathIndex = pathArrayString.split('.').length - 1;
            const oldCopyFlattenedTree = deepCopy(flattenedTree);
            // find all matches in flattenedTree and shift the index of the children   -   children.0.children.0
            for (const pathKey in flattenedTree) {
                const path = pathKey;
                const match = path.substring(0, pathArrayString.length - 1);

                if (match === pathArrayString.substring(0, pathArrayString.length - 1)) {
                    const pathArray = path.split('.');
                    const oldValue = pathArray[pathIndex];
                    const newValue = parseInt(oldValue) + 1 + '';
                    pathArray[pathIndex] = newValue;

                    const oldPathValue = oldCopyFlattenedTree[pathKey];

                    // delete flattenedTree[pathKey];
                    const newPathArrayString = pathArray.join('.');

                    flattenedTree[newPathArrayString] = oldPathValue;
                }

                // const value = flattenedTree[pathKey];
                // const newPath = pathArrayString + "." + path;
                // flattenedTree[newPath] = value;
            }

            for (const pathKey in flattenedTree2) {
                const path = pathKey;
                const value = flattenedTree2[pathKey];
                const newPath = pathArrayString + '.' + path;
                flattenedTree[newPath] = value;
            }
        }
        // get all the paths of the tree nodes
        this.setAllPathsOfTree(flattenedTree);

        const unflattenTree2 = unFlatten(flattenedTree);

        return unflattenTree2;
    }

    private setAllPathsOfTree(flattenedTree: any) {
        for (const pathKey in flattenedTree) {
            const path = pathKey;
            const pathArray = path.split('.');
            const lastValueOfPath = pathArray[pathArray.length - 1];
            const node = flattenedTree[pathKey];
            let pathArrayString = '';
            if (lastValueOfPath === 'id') {
                // pop last item off pathArray
                pathArray.pop();
                // console.log("pathArray: ", pathArray);
                for (let j = 0; j < pathArray.length; j++) {
                    pathArrayString += pathArray[j] + '.';
                }
                const newPath = pathArrayString + 'path';
                flattenedTree[newPath] = newPath;
            }
        }
    }

    flattenTree(tree: any) {
        const flattenTree = flatten(tree);
        return flattenTree;
    }
}

export interface InheritanceObjects {
    primaryPositionSecurityGroups: any[];
    secondaryPositionSecurityGroups: SecondaryPositionSecurityGroup[];
}

export interface SecondaryPositionSecurityGroup {
    assignedTo: string;
    policies: any[];
    featureDetails: FeatureDetails;
    roles: any[];
    features: string[];
    name: string;
    key: string;
    assigned: boolean;
    orgPosition: OrgPosition;
    multiUsers?: boolean;
}

interface OrgPosition {
    partKey: string;
    id: number;
    key: string;
    path: string;
    name: string;
    depth: number;
    parentRef: string;
    parentPartKey: string;
    children?: any[];
}

interface FeatureDetails {
    [key: string]: {
        name: string;
        permissions: string[];
        key: string;
    };
}

interface LevelUp {
    background: string;
    color: string;
    selected: boolean;
}

export interface UserDetails {
    key: string;
    name: string;
    positions: Position[];
    primaryPositionSecurityGroupsForUser: SecurityGroupDetails[];
    secondaryPositionsSecurityGroups: any[];
    positionsObjects: PositionsObjects;
    photoURL?: string;
    photoThumbnail?: string;
    profileColor?: string;
    /** True when the user is in pendingAssignedToArray (awaiting approval) for the current position context. */
    isPending?: boolean;
    /** Set of position partKeys where this user is pending (used by builder to resolve isPending per-position). */
    pendingPositionPartKeys?: Set<string>;
    /** True when this user has not yet accepted their org invitation (orgUsers.{uid}.status === 'pending').
     *  Admins must NOT be able to approve post-level assignments until the user accepts. */
    awaitingAcceptance?: boolean;
}

export interface Position {
    partKey: string;
    parentPartKey: string;
    depth: number;
    parentRef: string;
    id: number;
    name: string;
    key: string;
    path: string;
    levelUp: LevelUp;
    type: string;
    expandedKey: string;
    securityGroupsArray: SecurityGroupDetails[];
    inheritanceObjects: InheritanceObjects;
    userDetailsArray: UserDetails[];
    parent: string;
    children?: Position[];
    parents?: Position[];
    heldFromAboveUid?: string;
    heldFromAboveUserName?: string;
    heldFromAboveColor?: string;
    heldFromAbovePartKey?: string;
    heldFromAboveIsPending?: boolean;
    pendingHfaUserDetailsArray?: UserDetails[];
    colour: {
        background: string;
        color: string;
        linkColor: string;
    };

    securityGroupsFlat: Record<string, any>;
    securityGroupsObject: Record<string, never> | FlatSecurityGroupObject;
}

interface PositionsObjects {
    [key: string]: {
        position: Position[];
        primaryPositionSecurityGroups: SecurityGroupDetails[];
        secondaryPositionsSecurityGroups: any[];
        featureDetails: FeatureDetails;
    };
}

export interface SecurityGroupDetails {
    assigned: boolean;
    assignedTo: string;
    featureDetails: FeatureDetails;
    features: string[];
    key: string;
    name: string;
    orgPosition: OrgPosition;

    /**
     * @deprecated -- removed in newer model.
     */
    multiUsers?: boolean;
    /**
     * @deprecated -- removed in newer model.
     */
    policies?: any[];
    /**
     * @deprecated -- removed in newer model.
     */
    roles?: any[];
}

export interface FlatSecurityGroupObject {
    background: string;
    children: FlatSecurityGroupObject[];
    color: string;
    data: {
        depth: number;
        expandedKey: string;
        key: string;
        name: string;
        type: string;
    };
    selected: boolean;
}

export interface TreeNode<T, K = any> {
    background: string;
    children: TreeNode<K, any>[];
    color: string;
    data: T;
    id: string;
    level: number;
    parent?: TreeNode<K, any>;
}
